// kernel/useVersions.ts
import { useCallback, useMemo, useRef } from 'react';
import { CloudAssetSource, GenerationOutputScope, InventoryEntityState, KernelHandle, Layer, OutputImageFormat, Rect, useVersionsReturnType, Workspace, WorkspaceVersion } from '#types/types';
import { v4 as uuidv4 } from 'uuid';
import { uploadFileToCloud, uploadImageDataToCloud } from '#lib/azure-client';
import { markGenerationTaskAccepted } from '#lib/generationAccept';
import { mergeLayersForRender, sortLayersByRenderOrder } from '#lib/layer-render-order';
import { getDrawingLayerPayload } from '#lib/drawing-layer';
import { requireWorkspaceProjectIdFromKernel } from '#lib/workspace-scope';
import { findMatchingVersionId } from '#lib/workspace-layer-sync';
import { extractFramesFromVideo, loadImageDataFromBlob, loadImageDataFromUrl } from '#lib/canvas-utils';
import { decideListVersionPromotion } from '#lib/studio-list-versioning';
import { resolveGenerationOutputScopeForView } from '#lib/generation-output-scope';

interface useVersionsProps {
  kernel: React.RefObject<KernelHandle | null>;
}

export interface ExternalStudioWorkspaceVersionDtoChange {
  entityId: string;
  eventType: 'CREATE' | 'UPDATE' | 'DELETE';
  previousEntity: Pick<InventoryEntityState<'StudioWorkspaceVersion'>, 'workspaceId' | 'projectId'> | null;
  nextEntity: Pick<InventoryEntityState<'StudioWorkspaceVersion'>, 'workspaceId' | 'projectId'> | null;
}

const normalizeExternalWorkspaceVersionIds = (
  workspace: Pick<InventoryEntityState<'StudioWorkspace'>, 'versionIds'> | null
): string[] => {
  const versionIds = workspace?.versionIds;
  if (!Array.isArray(versionIds)) return [];
  return versionIds
    .filter((versionId): versionId is string => typeof versionId === 'string' && versionId.trim().length > 0)
    .map((versionId) => versionId.trim());
};

const resolveExternalWorkspaceVersionWorkspaceId = (
  version: Pick<InventoryEntityState<'StudioWorkspaceVersion'>, 'workspaceId'> | null
): string => {
  if (!version || typeof version.workspaceId !== 'string') return '';
  return version.workspaceId.trim();
};

const normalizeExternalWorkspaceVersionTimestamp = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
    const parsedDate = Date.parse(value);
    if (Number.isFinite(parsedDate)) return parsedDate;
  }
  return 0;
};

const normalizeExternalWorkspaceVersionStatus = (value: unknown): WorkspaceVersion['status'] => {
  if (value === 'VALIDATED' || value === 'REJECTED' || value === 'ARCHIVED') return value;
  return 'CANDIDATE';
};

const normalizeExternalWorkspaceVersionLayerVisibility = (
  value: unknown
): WorkspaceVersion['layerVisibility'] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const layerId = Reflect.get(entry, 'id');
      if (typeof layerId !== 'string' || layerId.trim().length === 0) return null;
      const visibleValue = Reflect.get(entry, 'visible');
      return {
        id: layerId.trim(),
        visible: visibleValue !== false,
      };
    })
    .filter((entry): entry is WorkspaceVersion['layerVisibility'][number] => Boolean(entry));
};

const toRuntimeWorkspaceVersionFromExternalEntity = (
  version: InventoryEntityState<'StudioWorkspaceVersion'> | null
): WorkspaceVersion | null => {
  if (!version || typeof version.id !== 'string' || version.id.trim().length === 0) return null;
  const versionId = version.id.trim();
  return {
    id: versionId,
    name: typeof version.name === 'string' && version.name.trim().length > 0 ? version.name : versionId,
    timestamp: normalizeExternalWorkspaceVersionTimestamp(version.timestamp),
    layerVisibility: normalizeExternalWorkspaceVersionLayerVisibility(version.layerVisibility),
    imageUrl: sanitizeVersionPreviewImageUrl(version.imageUrl) ?? undefined,
    isRenderCacheValid: version.isRenderCacheValid === true,
    renderStateHash: typeof version.renderStateHash === 'string' && version.renderStateHash.trim().length > 0
      ? version.renderStateHash
      : undefined,
    status: normalizeExternalWorkspaceVersionStatus(version.status),
    parentId: typeof version.parentVersionId === 'string' && version.parentVersionId.trim().length > 0
      ? version.parentVersionId
      : null,
    rejectionReason: typeof version.rejectionReason === 'string'
      ? version.rejectionReason
      : null,
    favorite: version.favorite === true,
    annotationComments: Array.isArray(version.annotationComments)
      ? version.annotationComments
      : [],
    lastAnnotationMaskUrl:
      typeof version.lastAnnotationMaskUrl === 'string' && version.lastAnnotationMaskUrl.trim().length > 0
        ? version.lastAnnotationMaskUrl
        : undefined,
  };
};

const getVersionVisibleLayerSignature = (version: WorkspaceVersion): string => {
  const visibleLayerIds = Array.isArray(version.layerVisibility)
    ? version.layerVisibility
        .filter((entry) => entry?.visible !== false && typeof entry.id === 'string' && entry.id.trim().length > 0)
        .map((entry) => entry.id.trim())
        .sort((left, right) => left.localeCompare(right))
    : [];
  return JSON.stringify(visibleLayerIds);
};

const hasVersionAnnotations = (version: WorkspaceVersion): boolean => {
  return (
    (Array.isArray(version.annotationComments) && version.annotationComments.length > 0) ||
    Boolean(version.annotationMask) ||
    Boolean(version.lastAnnotationMaskUrl)
  );
};

const normalizeVersionParentId = (parentId: string | null | undefined): string | null => {
  return typeof parentId === 'string' && parentId.trim().length > 0 ? parentId.trim() : null;
};

const normalizeVersionIds = (versionIds: readonly string[]): string[] =>
  Array.from(new Set(versionIds.map((id) => id.trim()).filter(Boolean)));

const resolveFirstSurvivingVersionAncestor = (
  parentId: string | null | undefined,
  versionsById: ReadonlyMap<string, WorkspaceVersion>,
  deletedIds: ReadonlySet<string>,
): string | null => {
  let cursorId = normalizeVersionParentId(parentId);
  const visited = new Set<string>();
  while (cursorId) {
    if (visited.has(cursorId)) return null;
    visited.add(cursorId);
    const version = versionsById.get(cursorId);
    if (!version) return null;
    if (!deletedIds.has(cursorId)) return cursorId;
    cursorId = normalizeVersionParentId(version.parentId);
  }
  return null;
};

const wouldCreateVersionCycle = (
  versions: WorkspaceVersion[],
  childVersionId: string | null | undefined,
  proposedParentId: string | null | undefined,
): boolean => {
  const normalizedChildId = typeof childVersionId === 'string' && childVersionId.trim().length > 0
    ? childVersionId.trim()
    : null;
  let cursorId = normalizeVersionParentId(proposedParentId);
  if (!cursorId) return false;
  if (normalizedChildId && cursorId === normalizedChildId) return true;

  const versionsById = new Map<string, WorkspaceVersion>();
  versions.forEach((version) => {
    if (typeof version.id === 'string' && version.id.trim().length > 0) {
      versionsById.set(version.id.trim(), version);
    }
  });

  const visited = new Set<string>();
  while (cursorId) {
    if (normalizedChildId && cursorId === normalizedChildId) return true;
    if (visited.has(cursorId)) return true;
    visited.add(cursorId);
    const cursorVersion = versionsById.get(cursorId);
    if (!cursorVersion) return false;
    cursorId = normalizeVersionParentId(cursorVersion.parentId);
  }

  return false;
};

const clearUnsafeVersionParents = (versions: WorkspaceVersion[]): WorkspaceVersion[] => {
  const versionsById = new Map<string, WorkspaceVersion>();
  versions.forEach((version) => {
    if (typeof version.id === 'string' && version.id.trim().length > 0) {
      versionsById.set(version.id.trim(), version);
    }
  });

  return versions.map((version) => {
    const parentId = normalizeVersionParentId(version.parentId);
    if (!parentId) return version.parentId === parentId ? version : { ...version, parentId };
    if (!wouldCreateVersionCycle(Array.from(versionsById.values()), version.id, parentId)) {
      return version.parentId === parentId ? version : { ...version, parentId };
    }
    const nextVersion = { ...version, parentId: null };
    versionsById.set(version.id, nextVersion);
    return nextVersion;
  });
};

// Removing a layer must rewrite version states, not delete every version that referenced it.
// Only after the rewrite do we collapse versions that now render to the same visible layer set.
export const reconcileWorkspaceVersionsAfterLayerRemoval = (
  workspace: Workspace,
  removedLayerIdInput: string
): Workspace => {
  const removedLayerId = removedLayerIdInput.trim();
  const versionsBefore = Array.isArray(workspace.versions) ? workspace.versions : [];
  if (!removedLayerId || versionsBefore.length === 0) return workspace;

  let didRemoveLayerReference = false;
  const removedLayerWasPresentByVersionId = new Map<string, boolean>();

  const versionsWithoutRemovedLayer = versionsBefore.map((version) => {
    const layerVisibility = Array.isArray(version.layerVisibility) ? version.layerVisibility : [];
    const nextLayerVisibility = layerVisibility.filter((entry) => entry.id !== removedLayerId);
    const removedFromVersion = nextLayerVisibility.length !== layerVisibility.length;
    removedLayerWasPresentByVersionId.set(version.id, removedFromVersion);
    if (!removedFromVersion) return version;
    didRemoveLayerReference = true;
    return {
      ...version,
      layerVisibility: nextLayerVisibility,
      isRenderCacheValid: false,
      renderStateHash: undefined,
    };
  });

  if (!didRemoveLayerReference) return workspace;

  const bucketsBySignature = new Map<string, WorkspaceVersion[]>();
  versionsWithoutRemovedLayer.forEach((version) => {
    const signature = getVersionVisibleLayerSignature(version);
    const bucket = bucketsBySignature.get(signature) ?? [];
    bucket.push(version);
    bucketsBySignature.set(signature, bucket);
  });

  const chooseVersionToKeep = (bucket: WorkspaceVersion[]): WorkspaceVersion => {
    return [...bucket].sort((left, right) => {
      const score = (version: WorkspaceVersion): number => {
        let value = 0;
        if (version.id === workspace.validatedVersionId) value += 1_000_000;
        if (version.id === workspace.activeVersionId) value += 500_000;
        if (!removedLayerWasPresentByVersionId.get(version.id)) value += 100_000;
        switch (version.status) {
          case 'VALIDATED':
            value += 40_000;
            break;
          case 'CANDIDATE':
            value += 30_000;
            break;
          case 'ARCHIVED':
            value += 20_000;
            break;
          case 'REJECTED':
            value += 10_000;
            break;
        }
        if (version.favorite) value += 1_000;
        if (hasVersionAnnotations(version)) value += 500;
        return value;
      };
      const scoreDelta = score(right) - score(left);
      if (scoreDelta !== 0) return scoreDelta;
      const leftTimestamp = Number.isFinite(left.timestamp) ? left.timestamp : 0;
      const rightTimestamp = Number.isFinite(right.timestamp) ? right.timestamp : 0;
      if (leftTimestamp !== rightTimestamp) return rightTimestamp - leftTimestamp;
      return left.id.localeCompare(right.id);
    })[0]!;
  };

  const keptVersionIds = new Set<string>();
  const duplicateVersionIdMap = new Map<string, string>();
  bucketsBySignature.forEach((bucket) => {
    const keptVersion = chooseVersionToKeep(bucket);
    keptVersionIds.add(keptVersion.id);
    bucket.forEach((version) => {
      duplicateVersionIdMap.set(version.id, keptVersion.id);
    });
  });

  const normalizedVersionById = new Map<string, WorkspaceVersion>();
  versionsWithoutRemovedLayer.forEach((version) => {
    normalizedVersionById.set(version.id, version);
  });

  const resolveParentId = (version: WorkspaceVersion): string | null => {
    let cursorId = normalizeVersionParentId(version.parentId);
    const visited = new Set<string>([version.id]);

    while (cursorId && !visited.has(cursorId)) {
      visited.add(cursorId);
      const mappedParentId = duplicateVersionIdMap.get(cursorId) ?? cursorId;
      if (mappedParentId !== version.id && keptVersionIds.has(mappedParentId)) {
        return wouldCreateVersionCycle(versionsWithoutRemovedLayer, version.id, mappedParentId)
          ? null
          : mappedParentId;
      }
      const cursorVersion = normalizedVersionById.get(cursorId);
      cursorId = normalizeVersionParentId(cursorVersion?.parentId);
    }

    return null;
  };

  const tentativeNormalizedVersions = versionsWithoutRemovedLayer
    .filter((version) => keptVersionIds.has(version.id))
    .map((version) => ({
      ...version,
      parentId: resolveParentId(version),
    }));
  // Parent remapping after dedupe can create cycles only visible in the final kept graph.
  const normalizedVersions = clearUnsafeVersionParents(tentativeNormalizedVersions);

  const normalizedKeptVersionIds = new Set(normalizedVersions.map((version) => version.id));
  const resolveVersionPointer = (versionId: string | null | undefined): string | null => {
    const normalizedVersionId = typeof versionId === 'string' && versionId.trim().length > 0
      ? versionId.trim()
      : null;
    if (!normalizedVersionId) return null;
    if (normalizedKeptVersionIds.has(normalizedVersionId)) return normalizedVersionId;
    const mappedVersionId = duplicateVersionIdMap.get(normalizedVersionId) ?? null;
    return mappedVersionId && normalizedKeptVersionIds.has(mappedVersionId) ? mappedVersionId : null;
  };

  const nextValidatedVersionId =
    resolveVersionPointer(workspace.validatedVersionId) ??
    normalizedVersions.find((version) => version.status === 'VALIDATED')?.id ??
    null;
  const nextActiveVersionId =
    resolveVersionPointer(workspace.activeVersionId) ??
    nextValidatedVersionId ??
    normalizedVersions[normalizedVersions.length - 1]?.id ??
    null;

  return {
    ...workspace,
    versions: normalizedVersions,
    validatedVersionId: nextValidatedVersionId,
    activeVersionId: nextActiveVersionId,
  };
};

export const syncWorkspaceRuntimeVersionsFromInventory = ({
  kernelHandle,
  workspaceId,
}: {
  kernelHandle: KernelHandle;
  workspaceId: string;
}): void => {
  const normalizedWorkspaceId = workspaceId.trim();
  if (!normalizedWorkspaceId) return;
  const inventory = kernelHandle.getInventory();
  const workspace = inventory.StudioWorkspace.registry[normalizedWorkspaceId];
  if (!workspace) return;
  const existingWorkspace = kernelHandle.getWorkspace(normalizedWorkspaceId);
  const existingRuntimeByVersionId = new Map<string, WorkspaceVersion>(
    (existingWorkspace?.versions ?? []).map((version) => [version.id, version])
  );

  const versionRegistry = inventory.StudioWorkspaceVersion.registry;
  const orderedVersionIds = normalizeExternalWorkspaceVersionIds(workspace);
  const collectedVersions: WorkspaceVersion[] = [];
  const seenVersionIds = new Set<string>();

  const collectVersion = (versionId: string) => {
    if (!versionId || seenVersionIds.has(versionId)) return;
    const version = versionRegistry[versionId];
    if (!version) return;
    const versionWorkspaceId = resolveExternalWorkspaceVersionWorkspaceId(version);
    if (!versionWorkspaceId || versionWorkspaceId !== normalizedWorkspaceId) return;
    const runtimeVersion = toRuntimeWorkspaceVersionFromExternalEntity(version);
    if (!runtimeVersion) return;
    const existingRuntimeVersion = existingRuntimeByVersionId.get(runtimeVersion.id);
    const cachedMaskFromWorkspace =
      existingWorkspace?.annotationMaskVersionId === runtimeVersion.id
        ? (existingWorkspace.annotationMask ?? null)
        : null;
    seenVersionIds.add(runtimeVersion.id);
    collectedVersions.push({
      ...runtimeVersion,
      annotationMask:
        existingRuntimeVersion?.annotationMask ??
        cachedMaskFromWorkspace ??
        null,
      annotationMaskIsDirty: existingRuntimeVersion?.annotationMaskIsDirty === true,
    });
  };

  orderedVersionIds.forEach(collectVersion);
  Object.values(versionRegistry).forEach((version) => {
    if (!version || typeof version.id !== 'string') return;
    if (resolveExternalWorkspaceVersionWorkspaceId(version) !== normalizedWorkspaceId) return;
    collectVersion(version.id.trim());
  });

  collectedVersions.sort((left, right) => {
    if (left.timestamp !== right.timestamp) return left.timestamp - right.timestamp;
    return left.id.localeCompare(right.id);
  });

  const keptVersionIds = new Set(collectedVersions.map((version) => version.id));
  const normalizedVersions = collectedVersions.map((version) => {
    const parentId = normalizeVersionParentId(version.parentId);
    return {
      ...version,
      parentId:
        parentId && keptVersionIds.has(parentId) && !wouldCreateVersionCycle(collectedVersions, version.id, parentId)
          ? parentId
          : null,
    };
  });

  kernelHandle.patchEntityRuntimeState('StudioWorkspace', normalizedWorkspaceId, {
    versions: normalizedVersions,
  });
};

export const handleExternalStudioWorkspaceVersionDtoChanges = ({
  kernel,
  changes,
}: {
  kernel: React.RefObject<KernelHandle | null>;
  changes: ExternalStudioWorkspaceVersionDtoChange[];
}): void => {
  const kernelHandle = kernel.current;
  if (!kernelHandle || changes.length === 0) return;

  const workspacesToSyncVersions = new Set<string>();

  changes.forEach((change) => {
    const workspaceId =
      resolveExternalWorkspaceVersionWorkspaceId(change.nextEntity) ||
      resolveExternalWorkspaceVersionWorkspaceId(change.previousEntity);
    if (workspaceId) {
      workspacesToSyncVersions.add(workspaceId);
    }
  });

  workspacesToSyncVersions.forEach((workspaceId) => {
    syncWorkspaceRuntimeVersionsFromInventory({
      kernelHandle,
      workspaceId,
    });
  });
};


const isLayerVisibleVisualState = (layer: Layer): boolean => {
  if (!layer || layer.visible === false) return false;
  if (layer.type === 'audio') return false;
  if (layer.type === 'video') {
    return Boolean(
      (typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0) ||
      (Array.isArray(layer.videoFramesUrls) && layer.videoFramesUrls.some((url) => typeof url === 'string' && url.trim().length > 0))
    );
  }
  return Boolean(
    layer.imageData ||
    (typeof layer.imageSrc === 'string' && layer.imageSrc.trim().length > 0) ||
    layer.type === 'svg'
  );
};

const pruneOldCandidates = (versions: WorkspaceVersion[]): WorkspaceVersion[] => {
  const MAX_CANDIDATES = 10;
  let updatedVersions = [...versions];
  // Only lineage candidates (those branched from a parent, i.e. Canvas
  // iterations) are subject to the cap. Independent ROOT candidates
  // (parentId === null) — e.g. List/marketing-mode outputs — are never pruned,
  // so the batch list keeps every generation. (Today's roots are always
  // VALIDATED, so this never changes Canvas behavior.)
  let candidates = updatedVersions.filter(v => v.status === 'CANDIDATE' && v.parentId != null);

  while (candidates.length >= MAX_CANDIDATES) {
    candidates.sort((a, b) => a.timestamp - b.timestamp);
    const oldestCandidate = candidates.shift();

    if (!oldestCandidate) {
      break;
    }

    const parentOfOldest = normalizeVersionParentId(oldestCandidate.parentId);
    const versionsWithoutOldest = updatedVersions.filter(v => v.id !== oldestCandidate.id);
    updatedVersions = versionsWithoutOldest.map(v => {
      if (v.parentId === oldestCandidate.id) {
        return {
          ...v,
          parentId: wouldCreateVersionCycle(versionsWithoutOldest, v.id, parentOfOldest) ? null : parentOfOldest,
        };
      }
      return v;
    });

    candidates = updatedVersions.filter(v => v.status === 'CANDIDATE' && v.parentId != null);
  }

  return updatedVersions;
};

const DEFAULT_VERSION_TARGET_PIXELS = 512 * 1024;
const DEFAULT_VERSION_OUTPUT_QUALITY = 0.9;

const formatToMimeType = (format: OutputImageFormat): string => {
  switch (format) {
    case 'png':
      return 'image/png';
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
  }
};

interface RenderVersionSnapshotOptions {
  workspaceId: string;
  layerVisibility: WorkspaceVersion['layerVisibility'];
  extraLayers?: Layer[];
  preferredAncestorVersionId?: string | null;
  targetPixels?: number;
  rect?: Rect;
  outputFormat?: OutputImageFormat;
  outputQuality?: number;
  assetSource?: CloudAssetSource;
}

interface AddCompletedGenerationOptions {
  newLayerForRendering?: Layer;
  targetPixels?: number;
  assetSource?: CloudAssetSource;
  fallbackImageUrl?: string | null;
  abortSignal?: AbortSignal;
  /** Captured generation destination; absent only for legacy non-task callers. */
  outputScope?: GenerationOutputScope;
  /** LIST self-validation loop: mark this output a hidden draft (see isLoopDraft). */
}

const createAbortError = (): Error => {
  const error = new Error('Operation aborted.');
  error.name = 'AbortError';
  return error;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

const VIDEO_URL_EXTENSION_PATTERN = /\.(mp4|mov|webm|m4v|mkv)(?:[?#]|$)/i;

function isLikelyVideoUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && VIDEO_URL_EXTENSION_PATTERN.test(url.trim());
}

function sanitizeVersionPreviewImageUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed || isLikelyVideoUrl(trimmed)) return null;
  return trimmed;
}

const captureVideoElementFrame = (videoElement: HTMLVideoElement | null | undefined): ImageData | null => {
  if (!videoElement || videoElement.readyState < 2) return null;
  const width = Math.max(videoElement.videoWidth, 1);
  const height = Math.max(videoElement.videoHeight, 1);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(videoElement, 0, 0, width, height);
    return ctx.getImageData(0, 0, width, height);
  } catch (error) {
    console.warn('[Versions] Failed to capture current video frame for version preview.', error);
    return null;
  }
};

const resolveVideoFrameImageData = async (
  layer: Layer,
  videoElement: HTMLVideoElement | null | undefined
): Promise<ImageData | null> => {
  const currentFrame = captureVideoElementFrame(videoElement);
  if (currentFrame) return currentFrame;

  const cachedFrameUrls = Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : [];
  const frameUrl = cachedFrameUrls
    .map((url) => sanitizeVersionPreviewImageUrl(url))
    .find((url): url is string => Boolean(url))
    ?? sanitizeVersionPreviewImageUrl(layer.imageSrc);
  if (frameUrl) {
    try {
      return await loadImageDataFromUrl(frameUrl);
    } catch (error) {
      console.warn(`[Versions] Failed to load cached video frame for layer ${layer.id}.`, error);
    }
  }

  if (typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0) {
    try {
      const extraction = await extractFramesFromVideo(layer.videoSrc, Number.MAX_SAFE_INTEGER);
      const firstFrame = extraction.frames[0]?.frameBlob;
      if (firstFrame) {
        return await loadImageDataFromBlob(firstFrame);
      }
    } catch (error) {
      console.warn(`[Versions] Failed to extract video frame for layer ${layer.id}.`, error);
    }
  }

  return null;
};

const createSnapshotLayerId = (layerId: string, index: number): string =>
  `__version_snapshot_${index}_${layerId}`;

const createSnapshotRasterLayer = (layer: Layer, imageData: ImageData, index: number): Layer => ({
  ...layer,
  id: createSnapshotLayerId(layer.id, index),
  type: undefined,
  imageData,
  visible: true,
  imageSrc: undefined,
  videoSrc: undefined,
  videoFramesUrls: undefined,
});

const createSnapshotPassthroughLayer = async (layer: Layer, index: number): Promise<Layer | null> => {
  if (layer.type === 'audio') return null;
  if (layer.type === 'video') return null;
  if (layer.imageData) {
    return {
      ...layer,
      id: createSnapshotLayerId(layer.id, index),
      visible: true,
    };
  }
  const imageUrl = sanitizeVersionPreviewImageUrl(layer.imageSrc);
  if (!imageUrl) return null;
  try {
    const imageData = await loadImageDataFromUrl(imageUrl);
    return createSnapshotRasterLayer(layer, imageData, index);
  } catch (error) {
    console.warn(`[Versions] Failed to load image layer ${layer.id} for video-aware snapshot.`, error);
    return null;
  }
};

// Layers decode asynchronously after load; the pixels loaded here serve this render only and never reach the store.
const loadUndecodedRasterLayers = async (layers: Layer[]): Promise<Layer[]> => {
  const loadedLayers: Layer[] = [];
  for (const layer of layers) {
    if (layer.type || layer.imageData || getDrawingLayerPayload(layer.params)) continue;
    const imageUrl = sanitizeVersionPreviewImageUrl(layer.imageSrc);
    if (!imageUrl) continue;
    try {
      loadedLayers.push({ ...layer, imageData: await loadImageDataFromUrl(imageUrl) });
    } catch (error) {
      console.warn(`[Versions] Failed to load image layer ${layer.id} for version snapshot; skipping it.`, error);
    }
  }
  return loadedLayers;
};

interface VersionRenderStateHashOptions {
  workspaceId: string;
  layerVisibility: WorkspaceVersion['layerVisibility'];
  rect?: Rect;
  extraLayers?: Layer[];
}

const useVersions = ({ kernel }: useVersionsProps): useVersionsReturnType => {
  const workspaceVersionEnsureInFlightRef = useRef<Map<string, Promise<string | null>>>(new Map());

  const setWorkspaceStrict = useCallback((
    workspaceIdInput: string,
    updater: Workspace | null | ((previousWorkspace: Workspace) => Workspace | null)
  ): void => {
    if (!kernel.current) return;
    const workspaceId = workspaceIdInput.trim();
    if (!workspaceId) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');
    kernel.current.setWorkspace(workspaceId, updater, projectId);
  }, [kernel]);

  const saveWorkspaceVersionAnnotation = useCallback(async (
    workspaceIdInput: string,
    versionIdInput?: string | null,
    options?: { signal?: AbortSignal }
  ): Promise<{
    annotationUploadAttempted: boolean;
    annotationUploadSucceeded: boolean;
  }> => {
    if (!kernel.current) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    const workspaceId = workspaceIdInput.trim();
    if (!workspaceId) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    const resolvedVersionId =
      typeof versionIdInput === 'string' && versionIdInput.trim().length > 0
        ? versionIdInput.trim()
        : (typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
          ? workspace.activeVersionId
          : (typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0
            ? workspace.validatedVersionId
            : null));

    if (!resolvedVersionId) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    const version = (workspace.versions ?? []).find((candidate) => candidate.id === resolvedVersionId) ?? null;
    if (!version) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    const shouldPersistAnnotation =
      version.annotationMaskIsDirty === true ||
      (Boolean(version.annotationMask) && !version.lastAnnotationMaskUrl);
    if (!shouldPersistAnnotation) {
      return { annotationUploadAttempted: false, annotationUploadSucceeded: false };
    }

    let annotationUploadAttempted = false;
    let annotationUploadSucceeded = false;
    let nextAnnotationMaskUrl: string | null =
      typeof version.lastAnnotationMaskUrl === 'string' ? version.lastAnnotationMaskUrl : null;

    if (version.annotationMask) {
      annotationUploadAttempted = true;
      try {
        const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');
        const projectOwnerId = kernel.current.getInventory().Project.registry[projectId]?.userId;
        const uploadedUrl = await uploadImageDataToCloud(
          version.annotationMask,
          'webp',
          options?.signal,
          projectOwnerId,
          0.7,
        );
        if (uploadedUrl) {
          nextAnnotationMaskUrl = uploadedUrl;
          annotationUploadSucceeded = true;
        }
      } catch (error) {
        console.error(`[saveWorkspaceVersionAnnotation] Failed to upload annotation mask for version ${resolvedVersionId}.`, error);
      }
    } else {
      // Explicit clear: no binary upload needed, but we still persist a null URL.
      nextAnnotationMaskUrl = null;
      annotationUploadSucceeded = true;
    }

    const shouldRemainDirty = shouldPersistAnnotation && !annotationUploadSucceeded;

    setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
      ...currentWorkspace,
      versions: (currentWorkspace.versions ?? []).map((candidate) => {
        if (candidate.id !== resolvedVersionId) return candidate;
        return {
          ...candidate,
          lastAnnotationMaskUrl: annotationUploadSucceeded ? nextAnnotationMaskUrl : candidate.lastAnnotationMaskUrl,
          annotationMaskIsDirty: shouldRemainDirty,
        };
      }),
    }));

    return {
      annotationUploadAttempted,
      annotationUploadSucceeded,
    };
  }, [kernel, setWorkspaceStrict]);


  const computeVersionRenderStateHash = useCallback(({
    workspaceId,
    layerVisibility,
    rect,
    extraLayers,
  }: VersionRenderStateHashOptions): string | null => {
    if (!kernel.current) return null;
    const { getWorkspace } = kernel.current;
    const workspace = getWorkspace(workspaceId);
    if (!workspace) return null;

    const layerRegistry = kernel.current.getLayerRegistry();
    const layersById = new Map<string, Layer>();
    Object.values(layerRegistry).forEach((layer) => {
      layersById.set(layer.id, layer);
    });
    (extraLayers ?? []).forEach((layer) => {
      layersById.set(layer.id, layer);
    });

    const visibleIds = layerVisibility
      .filter(item => item.visible)
      .map(item => item.id);

    const renderLayers = sortLayersByRenderOrder(
      visibleIds
        .map(layerId => layersById.get(layerId))
        .filter((layer): layer is Layer => Boolean(layer))
        .filter(layer => layer.type !== 'audio')
    );

    const renderRect = rect ?? workspace.frameRect;
    const payload = {
      workspaceId,
      rect: [renderRect.x, renderRect.y, renderRect.w, renderRect.h],
      layers: renderLayers.map(layer => ({
        id: layer.id,
        type: layer.type ?? null,
        contentVersion: layer.contentVersion ?? null,
        renderOrder: layer.renderOrder ?? null,
        scale: layer.scale ?? null,
        opacity: layer.opacity ?? null,
        rect: [layer.rect.x, layer.rect.y, layer.rect.w, layer.rect.h],
        imageSrc: layer.imageSrc ?? null,
        videoSrc: layer.videoSrc ?? null,
        videoFramesUrls: Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : null,
        timelineStartMs: layer.timelineStartMs ?? null,
        timelineEndMs: layer.timelineEndMs ?? null,
        timelineSourceInMs: layer.timelineSourceInMs ?? null,
      })),
    };

    return JSON.stringify(payload);
  }, [kernel]);

  const renderAndUploadVersionSnapshot = useCallback(async ({
    workspaceId,
    layerVisibility,
    extraLayers,
    preferredAncestorVersionId = null,
    targetPixels = DEFAULT_VERSION_TARGET_PIXELS,
    rect,
    outputFormat = 'webp',
    outputQuality = DEFAULT_VERSION_OUTPUT_QUALITY,
    assetSource = 'version_view',
  }: RenderVersionSnapshotOptions): Promise<string | null> => {
    if (!kernel.current) return null;
    const normalizedOutputFormat: OutputImageFormat = 'webp';
    if (outputFormat !== 'webp') {
      console.warn(`[Versions] Version snapshots are forced to webp. Ignoring requested format "${outputFormat}".`);
    }

    const { getWorkspace, renderWorkspaceArea, registerCloudAssets } = kernel.current;
    const workspace = getWorkspace(workspaceId);
    const shouldLogPerf = kernel.current.getUserPreferences()?.performanceHudEnabled === true;

    if (!workspace) {
      console.error(`[Versions] Cannot render snapshot for non-existent workspace ${workspaceId}`);
      return null;
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');
    const projectOwnerId = kernel.current.getInventory().Project.registry[projectId]?.userId;

    const normalizedRect = rect ?? workspace.frameRect;

    const visibleLayerIds = layerVisibility
      .filter(item => item.visible)
      .map(item => item.id);

    const layerRegistry = kernel.current.getLayerRegistry();
    const layersById = new Map<string, Layer>();
    Object.values(layerRegistry).forEach((layer) => {
      layersById.set(layer.id, layer);
    });
    (extraLayers ?? []).forEach((layer) => {
      layersById.set(layer.id, layer);
    });

    const visibleSourceLayers = sortLayersByRenderOrder(
      visibleLayerIds
        .map(layerId => layersById.get(layerId))
        .filter((layer): layer is Layer => Boolean(layer))
        .filter(layer => layer.type !== 'audio')
    );
    const hasVisibleVideoLayer = visibleSourceLayers.some((layer) => layer.type === 'video');

    let effectiveExtraLayers: Layer[] | undefined;
    let targetRenderableLayerIds: string[];

    if (hasVisibleVideoLayer) {
      const snapshotLayers: Layer[] = [];
      for (let index = 0; index < visibleSourceLayers.length; index += 1) {
        const layer = visibleSourceLayers[index];
        if (!layer) continue;
        if (layer.type === 'video') {
          const frameImageData = await resolveVideoFrameImageData(layer, kernel.current.videoRefs.current[layer.id]);
          if (!frameImageData) {
            console.warn(`[Versions] Unable to resolve a still frame for video layer ${layer.id}; skipping it in version preview.`);
            continue;
          }
          snapshotLayers.push(createSnapshotRasterLayer(layer, frameImageData, index));
          continue;
        }

        const snapshotLayer = await createSnapshotPassthroughLayer(layer, index);
        if (snapshotLayer) {
          snapshotLayers.push(snapshotLayer);
        }
      }

      effectiveExtraLayers = snapshotLayers;
      targetRenderableLayerIds = snapshotLayers.map((layer) => layer.id);
    } else {
      const workspaceLayerIdSet = new Set(
        (workspace.layerIds ?? [])
          .filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
      );
      const registryBackfilledLayers = visibleSourceLayers.filter((layer) => !workspaceLayerIdSet.has(layer.id));
      effectiveExtraLayers = (() => {
        const merged = [...(extraLayers ?? []), ...registryBackfilledLayers];
        if (merged.length === 0) return undefined;
        const byId = new Map<string, Layer>();
        merged.forEach((layer) => byId.set(layer.id, layer));
        return Array.from(byId.values());
      })();
      targetRenderableLayerIds = visibleSourceLayers.map(layer => layer.id);
    }

    if (visibleLayerIds.length > 0 && targetRenderableLayerIds.length === 0) {
      console.warn(`[Versions] Snapshot skipped for workspace ${workspaceId}: no visible renderable layers could be resolved.`);
      return null;
    }
    const layerIdsToRender = targetRenderableLayerIds.length > 0 ? targetRenderableLayerIds : undefined;
    const resolveRenderExtraLayers = async (layerIds: string[]): Promise<Layer[] | undefined> => {
      const loadedLayers = await loadUndecodedRasterLayers(
        layerIds.map((layerId) => layersById.get(layerId)).filter((layer): layer is Layer => Boolean(layer))
      );
      return loadedLayers.length > 0 ? mergeLayersForRender(effectiveExtraLayers ?? [], loadedLayers) : effectiveExtraLayers;
    };

    if (!layerIdsToRender) {
      console.warn(`[Versions] No visible layers provided for snapshot in workspace ${workspaceId}. Rendering empty frame.`);
    }

    const versionsById = new Map((workspace.versions || []).map(version => [version.id, version]));
    type ReuseDiagnostic = {
      reusable: boolean;
      reason: 'no_image_url' | 'no_expected_hash' | 'hash_match' | 'hash_mismatch' | 'legacy_hashless_cache_valid' | 'legacy_hashless_cache_invalid';
      expectedHashPresent: boolean;
      versionHashPresent: boolean;
      cacheValid: boolean;
    };
    const getVersionReuseDiagnostic = (version: WorkspaceVersion): ReuseDiagnostic => {
      if (!sanitizeVersionPreviewImageUrl(version.imageUrl)) {
        return {
          reusable: false,
          reason: 'no_image_url',
          expectedHashPresent: false,
          versionHashPresent: typeof version.renderStateHash === 'string' && version.renderStateHash.length > 0,
          cacheValid: Boolean(version.isRenderCacheValid),
        };
      }

      const expectedHash = computeVersionRenderStateHash({
        workspaceId,
        layerVisibility: version.layerVisibility,
        rect: normalizedRect,
      });
      const expectedHashPresent = typeof expectedHash === 'string' && expectedHash.length > 0;
      const versionHashPresent = typeof version.renderStateHash === 'string' && version.renderStateHash.length > 0;
      const cacheValid = Boolean(version.isRenderCacheValid);

      if (!expectedHashPresent) {
        return {
          reusable: cacheValid,
          reason: 'no_expected_hash',
          expectedHashPresent,
          versionHashPresent,
          cacheValid,
        };
      }

      if (versionHashPresent) {
        const hashMatches = version.renderStateHash === expectedHash;
        return {
          reusable: hashMatches,
          reason: hashMatches ? 'hash_match' : 'hash_mismatch',
          expectedHashPresent,
          versionHashPresent,
          cacheValid,
        };
      }

      return {
        reusable: cacheValid,
        reason: cacheValid ? 'legacy_hashless_cache_valid' : 'legacy_hashless_cache_invalid',
        expectedHashPresent,
        versionHashPresent,
        cacheValid,
      };
    };
    const isVersionPreviewReusable = (version: WorkspaceVersion): boolean => {
      return getVersionReuseDiagnostic(version).reusable;
    };

    let fastPathSkipReason: string | null = null;
    const setFastPathSkipReason = (reason: string) => {
      if (!fastPathSkipReason) {
        fastPathSkipReason = reason;
      }
    };

    const findClosestRenderableAncestor = (startVersionId: string | null): WorkspaceVersion | null => {
      let cursor = startVersionId;
      const visitedVersionIds = new Set<string>();
      while (cursor) {
        if (visitedVersionIds.has(cursor)) {
          console.warn('[Versions] Snapshot ancestor lineage cycle detected.', {
            workspaceId,
            startVersionId,
            repeatedVersionId: cursor,
            visitedVersionIds: Array.from(visitedVersionIds),
          });
          return null;
        }
        visitedVersionIds.add(cursor);
        const candidate = versionsById.get(cursor);
        if (!candidate) break;
        if (isVersionPreviewReusable(candidate)) {
          return candidate;
        }
        cursor = candidate.parentId ?? null;
      }
      return null;
    };

    const closestAncestor = findClosestRenderableAncestor(preferredAncestorVersionId);
    const preferredAncestor = preferredAncestorVersionId ? versionsById.get(preferredAncestorVersionId) ?? null : null;

    if (!closestAncestor) {
      setFastPathSkipReason('no_reusable_ancestor');
      if (shouldLogPerf && preferredAncestor) {
        const preferredDiag = getVersionReuseDiagnostic(preferredAncestor);
        const inspectedAncestors: Array<Record<string, unknown>> = [];
        let cursor = preferredAncestor.parentId ?? null;
        let depth = 0;
        while (cursor && depth < 5) {
          const candidate = versionsById.get(cursor);
          if (!candidate) break;
          const diag = getVersionReuseDiagnostic(candidate);
          inspectedAncestors.push({
            id: candidate.id,
            reason: diag.reason,
            reusable: diag.reusable,
            expectedHashPresent: diag.expectedHashPresent,
            versionHashPresent: diag.versionHashPresent,
            cacheValid: diag.cacheValid,
          });
          cursor = candidate.parentId ?? null;
          depth += 1;
        }
        console.log('[Perf][version-snapshot] reuse-diagnostic', {
          workspaceId,
          preferredAncestorVersionId,
          preferredAncestorDiagnostic: {
            reason: preferredDiag.reason,
            reusable: preferredDiag.reusable,
            expectedHashPresent: preferredDiag.expectedHashPresent,
            versionHashPresent: preferredDiag.versionHashPresent,
            cacheValid: preferredDiag.cacheValid,
          },
          inspectedAncestors,
          frameRect: normalizedRect,
        });
      }
    }
    if (!hasVisibleVideoLayer && closestAncestor?.imageUrl && layerIdsToRender) {
      const ancestorVisibleRenderableIds = sortLayersByRenderOrder(
        closestAncestor.layerVisibility
          .filter(item => item.visible)
          .map(item => layersById.get(item.id))
          .filter((layer): layer is Layer => Boolean(layer))
          .filter(layer => layer.type !== 'audio')
      ).map(layer => layer.id);

      const targetLayerSet = new Set(layerIdsToRender);
      const ancestorLayerSet = new Set(ancestorVisibleRenderableIds);
      const ancestorIsSubset = ancestorVisibleRenderableIds.every(layerId => targetLayerSet.has(layerId));
      const missingLayerIds = layerIdsToRender.filter(layerId => !ancestorLayerSet.has(layerId));
      if (!ancestorIsSubset) {
        setFastPathSkipReason('ancestor_not_subset_of_target');
      }

      let missingAreTopmost = true;
      if (missingLayerIds.length > 0) {
        const missingSet = new Set(missingLayerIds);
        let seenMissing = false;
        for (const layerId of layerIdsToRender) {
          if (missingSet.has(layerId)) {
            seenMissing = true;
            continue;
          }
          if (seenMissing) {
            missingAreTopmost = false;
            break;
          }
        }
      }
      if (!missingAreTopmost) {
        setFastPathSkipReason('missing_layers_not_topmost');
      }

      if (ancestorIsSubset && missingAreTopmost) {
        try {
          if (missingLayerIds.length === 0) {
            if (shouldLogPerf) {
              console.log('[Perf][version-snapshot] ancestor-reuse-direct', {
                workspaceId,
                ancestorVersionId: closestAncestor.id,
                targetLayerCount: layerIdsToRender.length,
              });
            }
            return closestAncestor.imageUrl;
          }

          const missingLayersRender = await renderWorkspaceArea(
            workspaceId,
            missingLayerIds,
            targetPixels,
            false,
            true,
            1,
            normalizedRect,
            normalizedOutputFormat,
            undefined,
            undefined,
            await resolveRenderExtraLayers(missingLayerIds),
            outputQuality
          );

          const missingImageData = missingLayersRender.imageData;
          if (!missingImageData) {
            throw new Error('[Versions] Missing layers render returned no imageData.');
          }

          const ancestorImageData = await loadImageDataFromUrl(closestAncestor.imageUrl);
          const composedCanvas = document.createElement('canvas');
          composedCanvas.width = missingImageData.width;
          composedCanvas.height = missingImageData.height;
          const composedCtx = composedCanvas.getContext('2d');
          if (!composedCtx) {
            throw new Error('[Versions] Failed to acquire context for composed version preview.');
          }

          const ancestorCanvas = document.createElement('canvas');
          ancestorCanvas.width = ancestorImageData.width;
          ancestorCanvas.height = ancestorImageData.height;
          const ancestorCtx = ancestorCanvas.getContext('2d');
          if (!ancestorCtx) {
            throw new Error('[Versions] Failed to acquire context for ancestor version preview.');
          }
          ancestorCtx.putImageData(ancestorImageData, 0, 0);
          composedCtx.drawImage(
            ancestorCanvas,
            0,
            0,
            ancestorCanvas.width,
            ancestorCanvas.height,
            0,
            0,
            composedCanvas.width,
            composedCanvas.height
          );

          const deltaCanvas = document.createElement('canvas');
          deltaCanvas.width = missingImageData.width;
          deltaCanvas.height = missingImageData.height;
          const deltaCtx = deltaCanvas.getContext('2d');
          if (!deltaCtx) {
            throw new Error('[Versions] Failed to acquire context for delta render.');
          }
          deltaCtx.putImageData(missingImageData, 0, 0);
          composedCtx.drawImage(deltaCanvas, 0, 0);

          const composedBlob = await new Promise<Blob | null>((resolve) => {
            composedCanvas.toBlob(
              resolve,
              formatToMimeType(normalizedOutputFormat),
              outputQuality,
            );
          });

          if (!composedBlob) {
            throw new Error('[Versions] Failed to serialize composed version preview.');
          }

          const fileName = `ws-version-${workspaceId}-${Date.now()}.${normalizedOutputFormat}`;
          const composedFile = new File([composedBlob], fileName, { type: formatToMimeType(normalizedOutputFormat) });
          const imageUrl = await uploadFileToCloud(composedFile, fileName, formatToMimeType(normalizedOutputFormat), undefined, projectOwnerId);

          if (imageUrl) {
            registerCloudAssets([imageUrl], assetSource);
            if (shouldLogPerf) {
              console.log('[Perf][version-snapshot] ancestor-composite', {
                workspaceId,
                ancestorVersionId: closestAncestor.id,
                missingLayerCount: missingLayerIds.length,
                targetLayerCount: layerIdsToRender.length,
              });
            }
            return imageUrl;
          }

          console.error('[Versions] Failed to upload composed version snapshot.');
          setFastPathSkipReason('composed_upload_failed');
        } catch (error) {
          setFastPathSkipReason('composite_exception');
          if (shouldLogPerf) {
            console.warn('[Perf][version-snapshot] ancestor-composite-fallback', {
              workspaceId,
              ancestorVersionId: closestAncestor.id,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      }
    }

    if (shouldLogPerf && fastPathSkipReason) {
      console.log('[Perf][version-snapshot] full-render-fallback', {
        workspaceId,
        preferredAncestorVersionId,
        ancestorVersionId: closestAncestor?.id ?? null,
        reason: fastPathSkipReason,
      });
    }

    try {
      const snapshotImage = await renderWorkspaceArea(
        workspaceId,
        layerIdsToRender,
        targetPixels,
        false,
        true,
        1,
        normalizedRect,
        normalizedOutputFormat,
        undefined,
        undefined,
        await resolveRenderExtraLayers(layerIdsToRender ?? []),
        outputQuality
      );

      if (!snapshotImage?.file) {
        console.error("[Versions] Failed to render workspace area for version thumbnail.");
        return null;
      }

      const fileName = `ws-version-${workspaceId}-${Date.now()}.${normalizedOutputFormat}`;
      const imageUrl = await uploadFileToCloud(snapshotImage.file, fileName, formatToMimeType(normalizedOutputFormat), undefined, projectOwnerId);

      if (!imageUrl) {
        console.error("[Versions] Failed to upload version thumbnail.");
        return null;
      }

      registerCloudAssets([imageUrl], assetSource);
      return imageUrl;
    } catch (error) {
      console.error(`[Versions] Error rendering snapshot for workspace ${workspaceId}:`, error);
      return null;
    }
  }, [kernel, computeVersionRenderStateHash]);
  
  const getActiveWorkspaceVersionId = useCallback((workspaceId: string): string | null => {
    if (!kernel.current) return null;

    const { getWorkspace } = kernel.current;
    const workspace = getWorkspace(workspaceId);
    if (!workspace) return null;
    return workspace.activeVersionId ?? null;
  }, [kernel]);

  const syncWorkspaceAnnotationCacheForVersion = useCallback((workspaceId: string, versionId: string | null) => {
    if (!kernel.current) return;

    setWorkspaceStrict(workspaceId, (currentWorkspace) => {
      if (!versionId) {
        return {
          ...currentWorkspace,
          annotationMask: null,
          annotationMaskVersionId: null,
        };
      }
      const version = (currentWorkspace.versions ?? []).find((candidate) => candidate.id === versionId) ?? null;
      const nextMask = version?.annotationMask ?? null;
      return {
        ...currentWorkspace,
        annotationMask: nextMask,
        annotationMaskVersionId: versionId,
      };
    });

    if (!versionId) return;
    const workspace = kernel.current.getWorkspace(workspaceId);
    const version = (workspace?.versions ?? []).find((candidate) => candidate.id === versionId) ?? null;
    if (!version || version.annotationMask || !version.lastAnnotationMaskUrl) return;

    void loadImageDataFromUrl(version.lastAnnotationMaskUrl)
      .then((maskData) => {
        setWorkspaceStrict(workspaceId, (currentWorkspace) => {
          const isStillActiveVersion = currentWorkspace.activeVersionId === versionId;
          let versionFound = false;
          const updatedVersions = (currentWorkspace.versions ?? []).map((candidate) => {
            if (candidate.id !== versionId) return candidate;
            versionFound = true;
            if (candidate.annotationMask) return candidate;
            return {
              ...candidate,
              annotationMask: maskData,
              annotationMaskIsDirty: false,
            };
          });
          if (!versionFound) return currentWorkspace;
          return {
            ...currentWorkspace,
            versions: updatedVersions,
            ...(isStillActiveVersion
              ? { annotationMask: maskData, annotationMaskVersionId: versionId }
              : {}),
          };
        });
      })
      .catch((error) => {
        console.error(`[Versions] Failed to decode annotation mask for version ${versionId}.`, error);
      });
  }, [kernel, setWorkspaceStrict]);

  const syncActiveWorkspaceVersionFromLayerVisibility = useCallback((workspaceIdInput: string | null | undefined): string | null => {
    if (!kernel.current) return null;
    const workspaceId = typeof workspaceIdInput === 'string' ? workspaceIdInput.trim() : '';
    if (!workspaceId) return null;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return null;

    const layerRegistry = kernel.current.getLayerRegistry();
    const visibleLayerIds = (workspace.layerIds ?? [])
      .map((layerId) => layerRegistry[layerId])
      .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer))
      .filter((layer) => layer.layerWorkspaceId === workspaceId && layer.visible !== false)
      .map((layer) => layer.id);

    const nextActiveVersionId = findMatchingVersionId(workspace, visibleLayerIds, {
      preferredVersionId: workspace.activeVersionId ?? null,
    });
    const previousActiveVersionId = workspace.activeVersionId ?? null;
    const resolvedActiveVersionId = nextActiveVersionId ?? previousActiveVersionId;
    if ((workspace.activeVersionId ?? null) !== resolvedActiveVersionId || workspace.activeVersionId === undefined) {
      setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        activeVersionId: resolvedActiveVersionId,
      }));
    }
    if (previousActiveVersionId !== resolvedActiveVersionId) {
      syncWorkspaceAnnotationCacheForVersion(workspaceId, resolvedActiveVersionId);
    }

    return nextActiveVersionId;
  }, [kernel, setWorkspaceStrict, syncWorkspaceAnnotationCacheForVersion]);

  const ensureCurrentStateAsVersion = useCallback(async (
    workspaceId: string,
    reason?: string,
    options?: { lightweight?: boolean; outputScope?: GenerationOutputScope; skipBuriedLayerCleanup?: boolean }
  ): Promise<string | null> => {
    if (!kernel.current) return null;

    const { getWorkspace, logUserAction } = kernel.current;

    let workspace = getWorkspace(workspaceId);
    if (!workspace) return null;

    // A captured PROJECT scope never creates a WorkspaceVersion. An explicit WORKSPACE scope
    // remains authoritative even if the operator changes views while the operation is pending.
    if (options?.outputScope?.kind === 'PROJECT') {
      return workspace.activeVersionId ?? null;
    }
    if (!options?.outputScope && kernel.current.getStudioViewMode() === 'marketing') {
      return workspace.activeVersionId ?? null;
    }

    const readVersionState = () => {
      const current = kernel.current?.getWorkspace(workspaceId);
      if (!kernel.current || !current) return null;
      const layerRegistry = kernel.current.getLayerRegistry();
      const layersInWorkspace = sortLayersByRenderOrder(
        (current.layerIds ?? [])
          .map((layerId) => layerRegistry[layerId])
          .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer))
      );
      const visibilityState = layersInWorkspace.map(l => ({ id: l.id, visible: l.visible }));
      const visibleLayerIds = layersInWorkspace.filter(l => l.visible).map(l => l.id);
      return {
        workspace: current,
        layersInWorkspace,
        visibilityState,
        renderStateHash: computeVersionRenderStateHash({ workspaceId, layerVisibility: visibilityState, rect: current.frameRect }),
        matchingVersionId: findMatchingVersionId(current, visibleLayerIds, { preferredVersionId: current.activeVersionId ?? null }),
      };
    };
    const reuseMatchingVersion = (current: Workspace, existingVersionId: string) => {
      if (reason !== 'Pre-Generation' && (current.activeVersionId ?? null) !== existingVersionId) {
        setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
          ...currentWorkspace,
          activeVersionId: existingVersionId,
        }));
        syncWorkspaceAnnotationCacheForVersion(workspaceId, existingVersionId);
      }
      return existingVersionId;
    };

    let state = readVersionState();
    if (!state) return null;
    const hasExistingVersionLineage =
      Boolean(state.workspace.validatedVersionId) ||
      (Array.isArray(state.workspace.versions) && state.workspace.versions.length > 0);
    const hasVisibleVisualState = state.layersInWorkspace.some(isLayerVisibleVisualState);
    if (!hasExistingVersionLineage && !hasVisibleVisualState) {
      return null;
    }
    if (state.matchingVersionId) return reuseMatchingVersion(state.workspace, state.matchingVersionId);

    // Only a version about to be created is cleaned: cleaning a saved state would itself make a new one.
    if (options?.skipBuriedLayerCleanup !== true) {
      try {
        await kernel.current.hideBuriedLayers(workspaceId);
      } catch (error) {
        console.error(`[Versions] Hiding buried layers failed for workspace ${workspaceId}.`, error);
        kernel.current?.handleError('Could not hide the layers covered by others; the version is saved as is.');
      }
      state = readVersionState();
      if (!state) return null;
      if (state.matchingVersionId) return reuseMatchingVersion(state.workspace, state.matchingVersionId);
    }
    workspace = state.workspace;
    const currentVisibilityState = state.visibilityState;
    const currentRenderStateHash = state.renderStateHash;

    logUserAction('Workspace Version Created', { workspaceId, reason });

    const lightweight = options?.lightweight === true;
    const imageUrl = lightweight
      ? null
      : await renderAndUploadVersionSnapshot({
          workspaceId,
          layerVisibility: currentVisibilityState,
          preferredAncestorVersionId: workspace.activeVersionId ?? workspace.validatedVersionId ?? null,
        });
    if (!lightweight && !imageUrl) {
      return null;
    }

    const parentId = workspace.activeVersionId ?? workspace.validatedVersionId ?? null;
    const sourceVersion = parentId
      ? (workspace.versions ?? []).find((version) => version.id === parentId) ?? null
      : null;

    const shouldPreserveCurrentStageParent = reason === 'Layer Visibility Toggle';
    // By default, we still collapse candidate-on-candidate chains to the nearest stable ancestor.
    // For visibility-driven protocol healing we preserve the current stage parent.
    let finalParentId = parentId;
    if (!shouldPreserveCurrentStageParent && finalParentId && workspace.versions) {
      const versionsMap = new Map(workspace.versions.map(v => [v.id, v]));
      let currentVersion = versionsMap.get(finalParentId);
      const visitedVersionIds = new Set<string>();

      while (currentVersion && currentVersion.status === 'CANDIDATE' && currentVersion.parentId) {
        if (visitedVersionIds.has(currentVersion.id)) {
          console.warn('[Versions] Candidate parent lineage cycle detected while creating workspace version.', {
            workspaceId,
            startVersionId: parentId,
            repeatedVersionId: currentVersion.id,
            visitedVersionIds: Array.from(visitedVersionIds),
          });
          finalParentId = null;
          break;
        }
        visitedVersionIds.add(currentVersion.id);
        finalParentId = currentVersion.parentId;
        currentVersion = versionsMap.get(finalParentId);
      }
    }

    const newVersionId = uuidv4();
    if (wouldCreateVersionCycle(workspace.versions ?? [], newVersionId, finalParentId)) {
      console.warn('[Versions] Unsafe workspace version parent skipped while creating a version.', {
        workspaceId,
        versionId: newVersionId,
        proposedParentId: finalParentId,
      });
      finalParentId = null;
    }

    const newVersion: WorkspaceVersion = {
      id: newVersionId,
      name: `State at ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      timestamp: Date.now(),
      layerVisibility: currentVisibilityState,
      imageUrl: imageUrl ?? undefined,
      isRenderCacheValid: lightweight ? false : true,
      renderStateHash: currentRenderStateHash ?? undefined,
      status: 'CANDIDATE',
      parentId: finalParentId,
      annotationComments: sourceVersion?.annotationComments
        ? sourceVersion.annotationComments.map((comment) => ({ ...comment }))
        : [],
      lastAnnotationMaskUrl: undefined,
      annotationMask: null,
      annotationMaskIsDirty: false,
    };

    setWorkspaceStrict(workspaceId, (currentWorkspace) => {
      let versions = [...(currentWorkspace.versions || [])];
      versions = pruneOldCandidates(versions);
      versions.push(newVersion);

      // If this is the very first version for this workspace, promote it immediately.
      if (!currentWorkspace.validatedVersionId) {
        newVersion.status = 'VALIDATED';
        return {
          ...currentWorkspace,
          versions,
          validatedVersionId: newVersion.id,
          activeVersionId: newVersion.id,
        };
      }
      return {
        ...currentWorkspace,
        versions,
        activeVersionId: newVersion.id,
        annotationMask: null,
        annotationMaskVersionId: null,
      };
    });

    return newVersion.id;
  }, [kernel, renderAndUploadVersionSnapshot, computeVersionRenderStateHash, syncWorkspaceAnnotationCacheForVersion]);

  const ensureWorkspaceVersionAfterCompositionCommit = useCallback(async (
    workspaceIdInput: string,
    reason = 'Workspace Composition Commit',
    options?: { outputScope?: GenerationOutputScope }
  ): Promise<string | null> => {
    if (!kernel.current) return null;

    const workspaceId = typeof workspaceIdInput === 'string' ? workspaceIdInput.trim() : '';
    if (!workspaceId) return null;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return null;

    // Capture the composition destination before entering the serialized ensure queue. A view
    // switch while an earlier ensure is pending must not redirect this composition.
    const capturedOutputScope = options?.outputScope
      ?? resolveGenerationOutputScopeForView(
        workspace.projectId,
        workspaceId,
        kernel.current.getStudioViewMode(),
      );
    if (capturedOutputScope?.kind === 'PROJECT') {
      return workspace.activeVersionId ?? null;
    }

    const previousEnsure = workspaceVersionEnsureInFlightRef.current.get(workspaceId) ?? Promise.resolve(null);
    const ensurePromise = previousEnsure
      .catch(() => null)
      .then(async (): Promise<string | null> => {
        if (!kernel.current) return null;
        const latestWorkspaceBeforeEnsure = kernel.current.getWorkspace(workspaceId);
        if (!latestWorkspaceBeforeEnsure) return null;

        const layerRegistry = kernel.current.getLayerRegistry();
        const hasVisibleVisualState = (latestWorkspaceBeforeEnsure.layerIds ?? [])
          .map((layerId) => layerRegistry[layerId])
          .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer))
          .some(isLayerVisibleVisualState);
        if (!hasVisibleVisualState) return null;

        return ensureCurrentStateAsVersion(
          workspaceId,
          reason,
          capturedOutputScope ? { outputScope: capturedOutputScope } : undefined,
        );
      });

    workspaceVersionEnsureInFlightRef.current.set(workspaceId, ensurePromise);
    void ensurePromise.finally(() => {
      if (workspaceVersionEnsureInFlightRef.current.get(workspaceId) === ensurePromise) {
        workspaceVersionEnsureInFlightRef.current.delete(workspaceId);
      }
    });
    return ensurePromise;
  }, [kernel, ensureCurrentStateAsVersion]);

  const ensureWritableAnnotationVersion = useCallback(async (workspaceIdInput: string): Promise<string | null> => {
    if (!kernel.current) return null;

    const workspaceId = workspaceIdInput.trim();
    if (!workspaceId) return null;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return null;

    const directVersionId =
      (typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0)
        ? workspace.activeVersionId
        : ((typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0)
          ? workspace.validatedVersionId
          : null);

    if (directVersionId && (workspace.versions ?? []).some((version) => version.id === directVersionId)) {
      return directVersionId;
    }

    try {
      return await ensureCurrentStateAsVersion(workspaceId, 'Auto ensure for annotation', { lightweight: true, skipBuriedLayerCleanup: true });
    } catch {
      return null;
    }
  }, [kernel, ensureCurrentStateAsVersion]);


  // Commit a freshly drawn annotation mask onto the workspace's writable version: ensure that
  // version exists, write the mask on the workspace and on the version together with its dirty
  // flag, then trigger the upload. The four fields are one invariant, so they belong here rather
  // than in whatever view produced the bitmap.
  const commitWorkspaceAnnotationMask = useCallback(async (
    workspaceIdInput: string,
    mask: ImageData
  ): Promise<string | null> => {
    if (!kernel.current) return null;
    const workspaceId = workspaceIdInput.trim();
    if (!workspaceId) return null;

    const targetVersionId = await ensureWritableAnnotationVersion(workspaceId);
    if (!targetVersionId) return null;

    setWorkspaceStrict(workspaceId, (workspace) => ({
      ...workspace,
      annotationMask: mask,
      annotationMaskVersionId: targetVersionId,
      versions: (workspace.versions ?? []).map((version) => (
        version.id !== targetVersionId
          ? version
          : { ...version, annotationMask: mask, annotationMaskIsDirty: true }
      )),
    }));

    void saveWorkspaceVersionAnnotation(workspaceId, targetVersionId).catch((error) => {
      console.error(`[useVersions.commitWorkspaceAnnotationMask] Failed to persist annotation mask for version "${targetVersionId}".`, error);
    });
    return targetVersionId;
  }, [kernel, ensureWritableAnnotationVersion, saveWorkspaceVersionAnnotation, setWorkspaceStrict]);

  const applyWorkspaceVersion = useCallback((workspaceId: string, versionId: string) => {
    if (!kernel.current) return;

    const { getWorkspace, executeAction, logUserAction } = kernel.current;

    const workspace = getWorkspace(workspaceId);
    const versionToApply = workspace?.versions?.find(s => s.id === versionId);

    if (!workspace || !versionToApply) {
      console.error(`Could not find workspace ${workspaceId} or version ${versionId} to apply.`);
      return;
    }

    logUserAction('Workspace Version Applied', { workspaceId, versionId, versionName: versionToApply.name });

    const layerRegistry = kernel.current.getLayerRegistry();
    const workspaceLayerIds = [...(workspace.layerIds ?? [])];
    const previousActiveVersionId = workspace.activeVersionId ?? null;
    const previousVisibilityByLayerId = new Map<string, boolean>(
      workspaceLayerIds.map((layerId) => {
        const layer = layerRegistry[layerId];
        return [layerId, layer?.visible !== false];
      })
    );

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');

    const redo = () => {
      const visibilityMap = new Map(versionToApply.layerVisibility.map(item => [item.id, item.visible]));
      let shouldHydrateVisibleLayers = false;
      workspaceLayerIds.forEach((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        if (!layer) return;
        const nextVisible = visibilityMap.get(layerId) ?? false;
        if (layer.visible === nextVisible) return;
        if (!layer.visible && nextVisible) {
          shouldHydrateVisibleLayers = true;
        }
        kernel.current?.setLayer(layerId, {
          ...layer,
          visible: nextVisible,
        });
      });
      setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        activeVersionId: versionId,
      }));
      syncWorkspaceAnnotationCacheForVersion(workspaceId, versionId);
      if (shouldHydrateVisibleLayers) {
        kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    const undo = () => {
      let shouldHydrateVisibleLayers = false;
      workspaceLayerIds.forEach((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        if (!layer) return;
        const previousVisible = previousVisibilityByLayerId.get(layerId);
        if (previousVisible === undefined || layer.visible === previousVisible) return;
        if (!layer.visible && previousVisible) {
          shouldHydrateVisibleLayers = true;
        }
        kernel.current?.setLayer(layerId, {
          ...layer,
          visible: previousVisible,
        });
      });
      setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        activeVersionId: previousActiveVersionId,
      }));
      syncWorkspaceAnnotationCacheForVersion(workspaceId, previousActiveVersionId);
      if (shouldHydrateVisibleLayers) {
        kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    executeAction({
      description: `Apply version: ${versionToApply.name}`,
      undo,
      redo,
    });
  }, [kernel, syncWorkspaceAnnotationCacheForVersion]);

  const deleteWorkspaceVersion = useCallback((workspaceId: string, versionId: string) => {
    if (!kernel.current) return;

    const { getWorkspace, executeAction, logUserAction, handleError } = kernel.current;

    const workspace = getWorkspace(workspaceId);
    const versionToDelete = workspace?.versions?.find(s => s.id === versionId);

    if (!workspace || !versionToDelete) {
      console.error(`Could not find workspace ${workspaceId} or version ${versionId} to delete.`);
      return;
    }

    // The marketing view has no validation lifecycle — outputs are independent and freely
    // deletable. The projectType fallback only serves legacy test/DTO callers without view state.
    const owningProjectForDelete = workspace.projectId
      ? kernel.current.getInventory().Project.registry[workspace.projectId]
      : null;
    const currentStudioViewMode = kernel.current.getStudioViewMode?.();
    const isListProjectDelete = currentStudioViewMode === 'marketing' || (
      currentStudioViewMode === undefined && owningProjectForDelete?.projectType === 'LIST'
    );
    if (versionToDelete.status === 'VALIDATED' && !isListProjectDelete) {
      handleError('Cannot delete validated or archived versions that form the main lineage.');
      return;
    }

    logUserAction('Workspace Version Deleted', { workspaceId, versionId, versionName: versionToDelete.name });

    const originalWorkspace = workspace;
    const parentOfDeleted = normalizeVersionParentId(versionToDelete.parentId);
    const originalLayerVisibilityByLayerId = new Map<string, boolean>(
      (workspace.layerIds ?? []).map((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        return [layerId, layer?.visible !== false];
      })
    );
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');

    const redo = () => {
      let shouldApplyReplacementVisibility = false;
      let nextActiveVersionLayerVisibility: WorkspaceVersion['layerVisibility'] = [];

      setWorkspaceStrict(workspaceId, (currentWorkspace) => {
        const versionsWithoutDeleted = (currentWorkspace.versions || [])
          .filter((version) => version.id !== versionId);
        const newVersions = versionsWithoutDeleted
          .map((version) => {
            if (version.parentId === versionId) {
              return {
                ...version,
                parentId: wouldCreateVersionCycle(versionsWithoutDeleted, version.id, parentOfDeleted) ? null : parentOfDeleted,
              };
            }
            return version;
          });
        const nextActiveVersionId =
          currentWorkspace.activeVersionId === versionId
            ? (parentOfDeleted ?? currentWorkspace.validatedVersionId ?? null)
            : currentWorkspace.activeVersionId;
        const nextActiveVersion = nextActiveVersionId
          ? newVersions.find((version) => version.id === nextActiveVersionId) ?? null
          : null;
        shouldApplyReplacementVisibility = currentWorkspace.activeVersionId === versionId;
        nextActiveVersionLayerVisibility = nextActiveVersion?.layerVisibility ?? [];
        return {
          ...currentWorkspace,
          versions: newVersions,
          activeVersionId: nextActiveVersionId,
          annotationMask: nextActiveVersion?.annotationMask ?? null,
          annotationMaskVersionId: nextActiveVersionId ?? null,
        };
      });

      if (shouldApplyReplacementVisibility) {
        const visibilityMap = new Map(
          nextActiveVersionLayerVisibility.map((entry) => [entry.id, entry.visible])
        );
        const currentWorkspace = kernel.current?.getWorkspace(workspaceId);
        const workspaceLayerIds = currentWorkspace?.layerIds ?? [];
        let shouldHydrateVisibleLayers = false;
        workspaceLayerIds.forEach((layerId) => {
          const layer = kernel.current?.getLayerRegistry()[layerId];
          if (!layer) return;
          const nextVisible = visibilityMap.get(layerId) ?? false;
          if (layer.visible === nextVisible) return;
          if (!layer.visible && nextVisible) {
            shouldHydrateVisibleLayers = true;
          }
          kernel.current?.setLayer(layerId, {
            ...layer,
            visible: nextVisible,
          });
        });
        if (shouldHydrateVisibleLayers) {
          kernel.current?.hydrateVisibleLayers(projectId);
        }
      }
    };

    const undo = () => {
      setWorkspaceStrict(workspaceId, originalWorkspace);

      const workspaceLayerIds = originalWorkspace.layerIds ?? [];
      let shouldHydrateVisibleLayers = false;
      workspaceLayerIds.forEach((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        if (!layer) return;
        const previousVisible = originalLayerVisibilityByLayerId.get(layerId);
        if (previousVisible === undefined || layer.visible === previousVisible) return;
        if (!layer.visible && previousVisible) {
          shouldHydrateVisibleLayers = true;
        }
        kernel.current?.setLayer(layerId, {
          ...layer,
          visible: previousVisible,
        });
      });
      if (shouldHydrateVisibleLayers) {
        kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    executeAction({
      description: `Delete version: ${versionToDelete.name}`,
      undo,
      redo,
    });
  }, [kernel]);

  const deleteWorkspaceVersions = useCallback((workspaceId: string, versionIds: string[]) => {
    const requestedIds = normalizeVersionIds(versionIds);
    const emptyResult = { deletedIds: [] as string[], skippedIds: requestedIds };
    if (!kernel.current || requestedIds.length === 0) return emptyResult;

    const { getWorkspace, executeAction, logUserAction, handleError } = kernel.current;
    const workspace = getWorkspace(workspaceId);
    if (!workspace) return emptyResult;

    const owningProject = workspace.projectId
      ? kernel.current.getInventory().Project.registry[workspace.projectId]
      : null;
    const currentStudioViewMode = kernel.current.getStudioViewMode?.();
    if (currentStudioViewMode !== 'marketing' && !(
      currentStudioViewMode === undefined && owningProject?.projectType === 'LIST'
    )) {
      handleError('Bulk output deletion is only available in List projects.');
      return emptyResult;
    }

    const originalVersions = [...(workspace.versions ?? [])];
    const originalVersionsById = new Map(originalVersions.map((version) => [version.id, version]));
    const deletedVersions = requestedIds
      .map((versionId) => originalVersionsById.get(versionId))
      .filter((version): version is WorkspaceVersion => Boolean(version));
    const deletedIds = deletedVersions.map((version) => version.id);
    const deletedIdSet = new Set(deletedIds);
    const skippedIds = requestedIds.filter((versionId) => !deletedIdSet.has(versionId));
    if (deletedIds.length === 0) return { deletedIds, skippedIds };

    const originalVersionIdSet = new Set(originalVersions.map((version) => version.id));
    const originalActiveVersionId = workspace.activeVersionId ?? null;
    const originalValidatedVersionId = workspace.validatedVersionId ?? null;
    const originalAnnotationMask = workspace.annotationMask ?? null;
    const originalAnnotationMaskVersionId = workspace.annotationMaskVersionId ?? null;
    const originalLayerVisibilityByLayerId = new Map<string, boolean>(
      (workspace.layerIds ?? []).map((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        return [layerId, layer?.visible !== false];
      }),
    );
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');
    let lastRedoActiveVersionId = originalActiveVersionId;
    let lastRedoValidatedVersionId = originalValidatedVersionId;
    let lastParentRepairs = new Map<string, { from: string | null; to: string | null }>();

    const redo = () => {
      let shouldApplyReplacementVisibility = false;
      let nextActiveVersionLayerVisibility: WorkspaceVersion['layerVisibility'] = [];
      setWorkspaceStrict(workspaceId, (currentWorkspace) => {
        const currentVersions = currentWorkspace.versions ?? [];
        const currentVersionsById = new Map(currentVersions.map((version) => [version.id, version]));
        const nextVersionsWithoutDeleted = currentVersions.filter((version) => !deletedIdSet.has(version.id));
        const parentRepairs = new Map<string, { from: string | null; to: string | null }>();
        const nextVersions = nextVersionsWithoutDeleted.map((version) => {
          const currentParentId = normalizeVersionParentId(version.parentId);
          const nextParentId = resolveFirstSurvivingVersionAncestor(
            currentParentId,
            currentVersionsById,
            deletedIdSet,
          );
          if (currentParentId === nextParentId) return version;
          parentRepairs.set(version.id, { from: currentParentId, to: nextParentId });
          return { ...version, parentId: nextParentId };
        });
        const nextVersionsById = new Map(nextVersions.map((version) => [version.id, version]));
        const nextValidatedVersionId = currentWorkspace.validatedVersionId
          && nextVersionsById.has(currentWorkspace.validatedVersionId)
          ? currentWorkspace.validatedVersionId
          : null;
        const currentActiveVersionId = currentWorkspace.activeVersionId ?? null;
        const nextActiveVersionId = !currentActiveVersionId || nextVersionsById.has(currentActiveVersionId)
          ? currentActiveVersionId
          : resolveFirstSurvivingVersionAncestor(
              currentVersionsById.get(currentActiveVersionId)?.parentId,
              currentVersionsById,
              deletedIdSet,
            ) ?? nextValidatedVersionId;
        const activeVersionChanged = currentActiveVersionId !== nextActiveVersionId;
        const nextActiveVersion = nextActiveVersionId
          ? nextVersionsById.get(nextActiveVersionId) ?? null
          : null;
        shouldApplyReplacementVisibility = activeVersionChanged;
        nextActiveVersionLayerVisibility = nextActiveVersion?.layerVisibility ?? [];

        lastParentRepairs = parentRepairs;
        lastRedoActiveVersionId = nextActiveVersionId;
        lastRedoValidatedVersionId = nextValidatedVersionId;
        return {
          ...currentWorkspace,
          versions: nextVersions,
          activeVersionId: nextActiveVersionId,
          validatedVersionId: nextValidatedVersionId,
          ...(activeVersionChanged
            ? {
                annotationMask: nextActiveVersion?.annotationMask ?? null,
                annotationMaskVersionId: nextActiveVersionId,
              }
            : {}),
        };
      });

      if (shouldApplyReplacementVisibility) {
        const visibilityMap = new Map(
          nextActiveVersionLayerVisibility.map((entry) => [entry.id, entry.visible]),
        );
        const currentWorkspace = kernel.current?.getWorkspace(workspaceId);
        let shouldHydrateVisibleLayers = false;
        (currentWorkspace?.layerIds ?? []).forEach((layerId) => {
          const layer = kernel.current?.getLayerRegistry()[layerId];
          if (!layer) return;
          const nextVisible = visibilityMap.get(layerId) ?? false;
          if (layer.visible === nextVisible) return;
          if (!layer.visible && nextVisible) shouldHydrateVisibleLayers = true;
          kernel.current?.setLayer(layerId, { ...layer, visible: nextVisible });
        });
        if (shouldHydrateVisibleLayers) kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    const undo = () => {
      let didRestoreActiveVersion = false;
      setWorkspaceStrict(workspaceId, (currentWorkspace) => {
        const repairedCurrentVersions = (currentWorkspace.versions ?? []).map((version) => {
          const repair = lastParentRepairs.get(version.id);
          if (!repair || normalizeVersionParentId(version.parentId) !== repair.to) return version;
          return { ...version, parentId: repair.from };
        });
        const restoredById = new Map(repairedCurrentVersions.map((version) => [version.id, version]));
        deletedVersions.forEach((version) => {
          if (!restoredById.has(version.id)) restoredById.set(version.id, version);
        });
        const restoredOriginalVersions = originalVersions
          .map((version) => restoredById.get(version.id))
          .filter((version): version is WorkspaceVersion => Boolean(version));
        const independentlyAddedVersions = repairedCurrentVersions
          .filter((version) => !originalVersionIdSet.has(version.id));
        const shouldRestoreActiveVersion = deletedIdSet.has(originalActiveVersionId ?? '')
          && (currentWorkspace.activeVersionId ?? null) === lastRedoActiveVersionId;
        const shouldRestoreValidatedVersion = deletedIdSet.has(originalValidatedVersionId ?? '')
          && (currentWorkspace.validatedVersionId ?? null) === lastRedoValidatedVersionId;
        didRestoreActiveVersion = shouldRestoreActiveVersion;

        return {
          ...currentWorkspace,
          versions: [...restoredOriginalVersions, ...independentlyAddedVersions],
          activeVersionId: shouldRestoreActiveVersion
            ? originalActiveVersionId
            : currentWorkspace.activeVersionId,
          validatedVersionId: shouldRestoreValidatedVersion
            ? originalValidatedVersionId
            : currentWorkspace.validatedVersionId,
          ...(shouldRestoreActiveVersion
            ? {
                annotationMask: originalAnnotationMask,
                annotationMaskVersionId: originalAnnotationMaskVersionId,
              }
            : {}),
        };
      });

      if (didRestoreActiveVersion) {
        const currentWorkspace = kernel.current?.getWorkspace(workspaceId);
        let shouldHydrateVisibleLayers = false;
        (currentWorkspace?.layerIds ?? []).forEach((layerId) => {
          const layer = kernel.current?.getLayerRegistry()[layerId];
          if (!layer) return;
          const previousVisible = originalLayerVisibilityByLayerId.get(layerId);
          if (previousVisible === undefined || layer.visible === previousVisible) return;
          if (!layer.visible && previousVisible) shouldHydrateVisibleLayers = true;
          kernel.current?.setLayer(layerId, { ...layer, visible: previousVisible });
        });
        if (shouldHydrateVisibleLayers) kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    logUserAction('Workspace Versions Deleted', {
      workspaceId,
      versionIds: deletedIds,
      count: deletedIds.length,
    });
    executeAction({
      description: `Delete ${deletedIds.length} workspace versions`,
      undo,
      redo,
    });
    return { deletedIds, skippedIds };
  }, [kernel, setWorkspaceStrict]);

  const promoteVersion = useCallback((workspaceId: string, versionIdToPromote: string) => {
    if (!kernel.current) return;
    const { getWorkspace, executeAction, logUserAction } = kernel.current;

    const workspace = getWorkspace(workspaceId);
    const versionToPromote = workspace?.versions?.find(v => v.id === versionIdToPromote);

    const owningProject = workspace?.projectId
      ? kernel.current.getInventory().Project.registry[workspace.projectId]
      : null;
    const promotionDecision = decideListVersionPromotion(
      workspace,
      versionToPromote,
      owningProject?.projectType,
      kernel.current.getStudioViewMode?.(),
    );
    if (promotionDecision === 'missing') {
      console.error(`Cannot promote version: workspace or version not found.`);
      return;
    }
    if (promotionDecision === 'skip') return;
    if (!workspace || !versionToPromote) return;

    const originalWorkspace = { ...workspace, versions: workspace.versions ? [...workspace.versions] : [] };

    if (versionToPromote.status === 'VALIDATED') {
      // This is a re-validation. Reject all current candidates.
      const candidatesToReject = (workspace.versions || []).filter(v => v.status === 'CANDIDATE');
      if (candidatesToReject.length === 0) {
        return; // Nothing to do.
      }

      logUserAction('Workspace Candidates Rejected on Re-validation', { workspaceId, versionId: versionIdToPromote });

      const redo = () => {
        setWorkspaceStrict(workspaceId, (currentWorkspace) => {
          const updatedVersions = (currentWorkspace.versions || []).map((version) => {
            if (version.status === 'CANDIDATE') {
              return { ...version, status: 'REJECTED' as const };
            }
            return version;
          });
          return { ...currentWorkspace, versions: updatedVersions };
        });
      };

      const undo = () => {
        setWorkspaceStrict(workspaceId, originalWorkspace);
      };

      executeAction({
        description: `Reject all candidate versions`,
        redo,
        undo,
      });
      return;
    }

    logUserAction('Workspace Version Promoted', { workspaceId, versionId: versionIdToPromote });
    void markGenerationTaskAccepted(versionIdToPromote);

    const redo = () => {
      setWorkspaceStrict(workspaceId, (currentWorkspace) => {
        const oldValidatedVersionId = currentWorkspace.validatedVersionId;
        const versions = currentWorkspace.versions || [];
        const parentIdOfPromoted = versions.find((version) => version.id === versionIdToPromote)?.parentId;

        const updatedVersions = versions.map((version) => {
          // Rule 1: Promote the target version.
          if (version.id === versionIdToPromote) {
            return { ...version, status: 'VALIDATED' as const };
          }

          // Rule 2: Demote/archive the old validated version.
          if (version.id === oldValidatedVersionId) {
            // If its parent is the one we are now promoting, it becomes a candidate again.
            if (version.parentId === versionIdToPromote) {
              return { ...version, status: 'CANDIDATE' as const };
            }
            // Otherwise, it's on a now-inactive branch and should be archived.
            return { ...version, status: 'ARCHIVED' as const };
          }

          // Rule 3: Reject sibling candidates of the newly promoted version.
          if (version.parentId === parentIdOfPromoted && version.id !== versionIdToPromote && version.status === 'CANDIDATE') {
            return { ...version, status: 'REJECTED' as const };
          }

          // Rule 4: Restore children of the newly promoted version that were previously rejected.
          if (version.parentId === versionIdToPromote && version.status === 'REJECTED') {
            return { ...version, status: 'CANDIDATE' as const };
          }

          return version;
        });

        return {
          ...currentWorkspace,
          versions: updatedVersions,
          validatedVersionId: versionIdToPromote,
        };
      });
    };

    const undo = () => {
      setWorkspaceStrict(workspaceId, originalWorkspace);
    };

    executeAction({
      description: `Set version as validated`,
      redo,
      undo,
    });
  }, [kernel]);

  const getVersionsForDisplay = useCallback((workspaceId: string, filterMode: 'PRIMARY' | 'ALL'): WorkspaceVersion[] => {
    if (!kernel.current) return [];
    const { getWorkspace } = kernel.current;

    const workspace = getWorkspace(workspaceId);
    if (!workspace || !workspace.versions) return [];

    const allVersions = workspace.versions;

    if (filterMode === 'ALL') {
      return allVersions;
    }

    // Logic for 'PRIMARY' mode
    const validatedVersionId = workspace.validatedVersionId;
    const validatedVersion = validatedVersionId ? allVersions.find(v => v.id === validatedVersionId) : null;

    if (!validatedVersion) {
        // If no version is validated, PRIMARY mode shows all root candidates.
        return allVersions.filter(v => v.parentId === null && v.status === 'CANDIDATE');
    }

    const primarySet = [validatedVersion];
    const directCandidates = allVersions.filter(v => v.parentId === validatedVersion.id && v.status === 'CANDIDATE');
    primarySet.push(...directCandidates);

    return primarySet;
  }, [kernel]);

  const navigateWorkspaceVersion = useCallback((direction: 'up' | 'down' | 'left' | 'right', workspaceIdInput: string) => {
    if (!kernel.current) return;
    const activeWorkspaceId = workspaceIdInput.trim();
    if (!activeWorkspaceId) {
      console.warn('navigateWorkspaceVersion requires an active workspace.');
      return;
    }
    const workspace = kernel.current.getWorkspace(activeWorkspaceId);
    if (!workspace) return;
    const allVersions = workspace.versions ?? [];
    if (allVersions.length === 0) return;

    const sortVersionsByTimeline = (left: WorkspaceVersion, right: WorkspaceVersion): number => {
      const leftTimestamp = Number.isFinite(left.timestamp) ? left.timestamp : 0;
      const rightTimestamp = Number.isFinite(right.timestamp) ? right.timestamp : 0;
      if (leftTimestamp !== rightTimestamp) return leftTimestamp - rightTimestamp;
      return left.id.localeCompare(right.id);
    };

    const versionsById = new Map<string, WorkspaceVersion>();
    allVersions.forEach((version) => {
      versionsById.set(version.id, version);
    });

    const validatedVersionId =
      typeof workspace.validatedVersionId === 'string' && versionsById.has(workspace.validatedVersionId)
        ? workspace.validatedVersionId
        : null;
    const activeVersionId =
      (() => {
        const currentActiveVersionId = getActiveWorkspaceVersionId(activeWorkspaceId);
        return currentActiveVersionId && versionsById.has(currentActiveVersionId)
          ? currentActiveVersionId
          : null;
      })();
    const fallbackFocusVersionId =
      validatedVersionId ??
      activeVersionId ??
      [...allVersions].sort(sortVersionsByTimeline).at(-1)?.id ??
      null;
    if (!fallbackFocusVersionId) return;

    const effectiveActiveVersionId = activeVersionId ?? fallbackFocusVersionId;

    if (direction === 'left' || direction === 'right') {
      // Horizontal navigation is scoped to one floor: its root version plus direct non-validated candidates.
      const activeVersion = versionsById.get(effectiveActiveVersionId);
      if (!activeVersion) return;

      const isStableFloorRoot = (version: WorkspaceVersion): boolean =>
        version.status === 'VALIDATED' || version.status === 'ARCHIVED';
      const isDirectNonValidatedCandidate = (version: WorkspaceVersion): boolean =>
        version.status !== 'VALIDATED' && version.status !== 'ARCHIVED';
      const normalizeParentId = (version: WorkspaceVersion): string | null =>
        typeof version.parentId === 'string' && version.parentId.trim().length > 0
          ? version.parentId.trim()
          : null;

      const floorRootVersion = (() => {
        if (isStableFloorRoot(activeVersion)) return activeVersion;
        const parentId = normalizeParentId(activeVersion);
        return (parentId ? versionsById.get(parentId) ?? null : null) ?? activeVersion;
      })();

      const versionsInCurrentFloor = [
        floorRootVersion,
        ...allVersions
          .filter((version) => version.id !== floorRootVersion.id)
          .filter((version) => normalizeParentId(version) === floorRootVersion.id)
          .filter(isDirectNonValidatedCandidate)
          .sort(sortVersionsByTimeline),
      ];

      let currentFloorPosition = versionsInCurrentFloor.findIndex((version) => version.id === effectiveActiveVersionId);
      if (currentFloorPosition < 0) {
        currentFloorPosition = versionsInCurrentFloor.findIndex((version) => version.id === floorRootVersion.id);
      }
      if (currentFloorPosition < 0) return;

      const delta = direction === 'right' ? 1 : -1;
      const targetPosition =
        (currentFloorPosition + delta + versionsInCurrentFloor.length) % versionsInCurrentFloor.length;
      const targetVersion = versionsInCurrentFloor[targetPosition];
      if (!targetVersion) return;
      applyWorkspaceVersion(activeWorkspaceId, targetVersion.id);
      return;
    }

    // Build the validated lineage (left column of the tree): focus -> ancestors.
    const lineageIds: string[] = [];
    const lineageVisited = new Set<string>();
    let lineageCursorId: string | null = fallbackFocusVersionId;
    while (lineageCursorId && !lineageVisited.has(lineageCursorId)) {
      lineageVisited.add(lineageCursorId);
      lineageIds.push(lineageCursorId);
      const nextParentId: string | null = versionsById.get(lineageCursorId)?.parentId ?? null;
      lineageCursorId = nextParentId && versionsById.has(nextParentId) ? nextParentId : null;
    }
    if (lineageIds.length === 0) return;

    const lineageIndexById = new Map<string, number>();
    lineageIds.forEach((versionId, index) => {
      lineageIndexById.set(versionId, index);
    });

    const resolveFloorIndexForVersion = (versionId: string | null): number => {
      if (!versionId) return 0;
      let cursorId: string | null = versionId;
      const visited = new Set<string>();
      while (cursorId && !visited.has(cursorId)) {
        visited.add(cursorId);
        const lineageIndex = lineageIndexById.get(cursorId);
        if (typeof lineageIndex === 'number') {
          return lineageIndex;
        }
        const nextParentId: string | null = versionsById.get(cursorId)?.parentId ?? null;
        cursorId = nextParentId && versionsById.has(nextParentId) ? nextParentId : null;
      }
      return 0;
    };

    const currentFloorIndex = resolveFloorIndexForVersion(effectiveActiveVersionId);
    const currentFloorAnchorVersionId = lineageIds[currentFloorIndex] ?? fallbackFocusVersionId;

    if (direction === 'up' || direction === 'down') {
      const lineageLength = lineageIds.length;
      if (lineageLength === 0) return;
      const delta = direction === 'up' ? -1 : 1;
      const targetFloorIndex = (currentFloorIndex + delta + lineageLength) % lineageLength;
      const targetVersionId = lineageIds[targetFloorIndex] ?? currentFloorAnchorVersionId;
      if (!targetVersionId) return;
      applyWorkspaceVersion(activeWorkspaceId, targetVersionId);
      return;
    }

  }, [
    kernel,
    getActiveWorkspaceVersionId,
    applyWorkspaceVersion,
  ]);

  const rejectWorkspaceVersions = useCallback((workspaceId: string, versionIds: string[], reason?: string | null): void => {
    if (!kernel.current) return;

    const { getWorkspace, executeAction, logUserAction, handleError } = kernel.current;
    const versionIdsSet = new Set(versionIds);
    const finalReason = reason ?? 'Rejected by user';

    const workspace = getWorkspace(workspaceId);

    if (!workspace) {
      console.error(`[Versions] Could not find workspace ${workspaceId} to reject versions.`);
      return;
    }
    const originalWorkspace = { ...workspace, versions: workspace.versions ? [...workspace.versions] : [] };

    const versionsToReject = (workspace.versions || []).filter(v => versionIdsSet.has(v.id));
    if (versionsToReject.length === 0) {
      return; // Nothing to do
    }

    logUserAction('Workspace Versions Rejected', { workspaceId, count: versionIds.length, reason: finalReason });
    const workspaceLayerIds = [...(workspace.layerIds ?? [])];
    const originalVisibilityByLayerId = new Map<string, boolean>(
      workspaceLayerIds.map((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        return [layerId, layer?.visible !== false];
      })
    );
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useVersions');

    const redo = () => {
      const activeVersionId = getActiveWorkspaceVersionId(workspaceId);
      const wasActiveVersionRejected = activeVersionId && versionIdsSet.has(activeVersionId);
      let versionsAfterReject: WorkspaceVersion[] = [];
      let validatedVersionIdAfterReject: string | null = null;

      setWorkspaceStrict(workspaceId, (currentWorkspace) => {
        const newVersions = (currentWorkspace.versions || []).map((version) => {
          if (versionIdsSet.has(version.id)) {
            if (version.status === 'VALIDATED' || version.status === 'ARCHIVED') {
              handleError(`Cannot reject version "${version.name}" because it is ${version.status}.`);
              return version;
            }
            if (version.status === 'CANDIDATE') {
              return { ...version, status: 'REJECTED' as const, rejectionReason: finalReason };
            }
          }
          return version;
        });

        versionsAfterReject = newVersions;
        validatedVersionIdAfterReject = currentWorkspace.validatedVersionId ?? null;
        return { ...currentWorkspace, versions: newVersions };
      });

      // 2. If active was rejected, calculate new layers state by reverting
      if (wasActiveVersionRejected) {
        const versionToApply = validatedVersionIdAfterReject
          ? versionsAfterReject.find((version) => version.id === validatedVersionIdAfterReject)
          : null;
        if (versionToApply) {
          const visibilityMap = new Map(versionToApply.layerVisibility.map((item) => [item.id, item.visible]));
          let shouldHydrateVisibleLayers = false;
          workspaceLayerIds.forEach((layerId) => {
            const layer = kernel.current?.getLayerRegistry()[layerId];
            if (!layer) return;
            const nextVisible = visibilityMap.get(layerId) ?? false;
            if (layer.visible === nextVisible) return;
            if (!layer.visible && nextVisible) {
              shouldHydrateVisibleLayers = true;
            }
            kernel.current?.setLayer(layerId, {
              ...layer,
              visible: nextVisible,
            });
          });
          if (shouldHydrateVisibleLayers) {
            kernel.current?.hydrateVisibleLayers(projectId);
          }
        }
        syncActiveWorkspaceVersionFromLayerVisibility(workspaceId);
      }

    };

    const undo = () => {
      setWorkspaceStrict(workspaceId, originalWorkspace);
      let shouldHydrateVisibleLayers = false;
      workspaceLayerIds.forEach((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        if (!layer) return;
        const previousVisible = originalVisibilityByLayerId.get(layerId);
        if (previousVisible === undefined || layer.visible === previousVisible) return;
        if (!layer.visible && previousVisible) {
          shouldHydrateVisibleLayers = true;
        }
        kernel.current?.setLayer(layerId, {
          ...layer,
          visible: previousVisible,
        });
      });
      if (shouldHydrateVisibleLayers) {
        kernel.current?.hydrateVisibleLayers(projectId);
      }
    };

    executeAction({
      description: `Reject ${versionsToReject.length} version(s)`,
      undo,
      redo,
    });
  }, [kernel, getActiveWorkspaceVersionId, syncActiveWorkspaceVersionFromLayerVisibility]);

  const invalidateWorkspaceVersionCache = useCallback((workspaceId: string | null | undefined) => {
    if (!kernel.current || !workspaceId) return;
    setWorkspaceStrict(workspaceId, (workspace) => {
      if (!workspace.versions) {
        return workspace;
      }
      return {
        ...workspace,
        versions: workspace.versions.map((version) => ({
          ...version,
          isRenderCacheValid: false,
        })),
      };
    });
  }, [kernel]);

  const updateVersionCache = useCallback((workspaceId: string, versionId: string, newImageUrl: string) => {
    if (!kernel.current) return;
    setWorkspaceStrict(workspaceId, (workspace) => {
      if (!workspace.versions) return workspace;
      const targetVersionIndex = workspace.versions.findIndex((version) => version.id === versionId);
      if (targetVersionIndex === -1) return workspace;
      const newVersions = [...workspace.versions];
      const updatedVersion: WorkspaceVersion = {
        ...newVersions[targetVersionIndex]!,
        imageUrl: newImageUrl,
        isRenderCacheValid: true,
      };
      newVersions[targetVersionIndex] = updatedVersion;
      return {
        ...workspace,
        versions: newVersions,
      };
    });
  }, [kernel]);

  const fetchVersionImageUrl = useCallback(async (
    workspace: Workspace,
    version: WorkspaceVersion,
    options?: { targetPixels?: number; assetSource?: CloudAssetSource; rect?: Rect }
  ): Promise<string | null> => {
    const expectedRenderStateHash = computeVersionRenderStateHash({
      workspaceId: workspace.id,
      layerVisibility: version.layerVisibility,
      rect: options?.rect ?? workspace.frameRect,
    });
    const hashMatches = expectedRenderStateHash !== null
      ? version.renderStateHash === expectedRenderStateHash
      : true;

    const cachedImageUrl = sanitizeVersionPreviewImageUrl(version.imageUrl);
    if (cachedImageUrl && hashMatches) {
      if (!version.isRenderCacheValid && kernel.current) {
            setWorkspaceStrict(workspace.id, (currentWorkspace) => ({
          ...currentWorkspace,
          versions: (currentWorkspace.versions || []).map((candidateVersion) => {
            if (candidateVersion.id !== version.id) return candidateVersion;
            return { ...candidateVersion, isRenderCacheValid: true };
          }),
        }));
      }
      return cachedImageUrl;
    }

    const newImageUrl = await renderAndUploadVersionSnapshot({
      workspaceId: workspace.id,
      layerVisibility: version.layerVisibility,
      preferredAncestorVersionId: version.parentId ?? null,
      targetPixels: options?.targetPixels,
      rect: options?.rect ?? workspace.frameRect,
      assetSource: options?.assetSource ?? 'version_view',
    });

    if (newImageUrl) {
      if (!kernel.current) return newImageUrl;
        setWorkspaceStrict(workspace.id, (currentWorkspace) => ({
        ...currentWorkspace,
        versions: (currentWorkspace.versions || []).map((candidateVersion) => {
          if (candidateVersion.id !== version.id) return candidateVersion;
          return {
            ...candidateVersion,
            imageUrl: newImageUrl,
            isRenderCacheValid: true,
            renderStateHash: expectedRenderStateHash ?? candidateVersion.renderStateHash,
          };
        }),
      }));

      return newImageUrl;
    }

    return sanitizeVersionPreviewImageUrl(version.imageUrl);
  }, [renderAndUploadVersionSnapshot, kernel, computeVersionRenderStateHash]);

  const addCompletedGenerationAsVersion = useCallback(async (
    workspaceId: string,
    versionData: Pick<WorkspaceVersion, 'name' | 'layerVisibility' | 'parentId'>,
    options: AddCompletedGenerationOptions = {}
  ): Promise<WorkspaceVersion | null> => {
    if (!kernel.current) return null;
    throwIfAborted(options.abortSignal);

    // Project-scoped generations are already materialized as detached Project Layers by
    // useGenerationTasks. They must never create a second WorkspaceVersion destination.
    if (options.outputScope?.kind === 'PROJECT') return null;

    // This helper is a Canvas versioning API. PROJECT output is rejected above; an old caller
    // without a scope is treated as WORKSPACE rather than inferred from projectType or view.
    const capturedWorkspaceId = options.outputScope?.kind === 'WORKSPACE'
      ? options.outputScope.workspaceId
      : workspaceId;
    const workspaceBeforeRender = kernel.current.getWorkspace(capturedWorkspaceId);
    let effectiveParentId = normalizeVersionParentId(versionData.parentId);
    const newVersionId = uuidv4();
    if (wouldCreateVersionCycle(workspaceBeforeRender?.versions ?? [], newVersionId, effectiveParentId)) {
      console.warn('[Versions] Unsafe workspace version parent skipped for completed generation.', {
        workspaceId,
        versionId: newVersionId,
        proposedParentId: effectiveParentId,
      });
      effectiveParentId = null;
    }

    const fallbackUrl = sanitizeVersionPreviewImageUrl(options.fallbackImageUrl);
    const snapshotUrl = await renderAndUploadVersionSnapshot({
      workspaceId: capturedWorkspaceId,
      layerVisibility: versionData.layerVisibility,
      extraLayers: options.newLayerForRendering ? [options.newLayerForRendering] : undefined,
      preferredAncestorVersionId: effectiveParentId ?? null,
      targetPixels: options.targetPixels,
      assetSource: options.assetSource ?? 'version_view',
    });
    throwIfAborted(options.abortSignal);

    const resolvedImageUrl = snapshotUrl ?? fallbackUrl ?? undefined;
    const nextRenderStateHash = computeVersionRenderStateHash({
      workspaceId: capturedWorkspaceId,
      layerVisibility: versionData.layerVisibility,
      extraLayers: options.newLayerForRendering ? [options.newLayerForRendering] : undefined,
    });
    const workspaceForAnnotations = kernel.current.getWorkspace(capturedWorkspaceId);
    const sourceAnnotationVersion = effectiveParentId
      ? (workspaceForAnnotations?.versions ?? []).find((version) => version.id === effectiveParentId) ?? null
      : null;
    throwIfAborted(options.abortSignal);

    const newVersion: WorkspaceVersion = {
      id: newVersionId,
      name: versionData.name,
      timestamp: Date.now(),
      layerVisibility: versionData.layerVisibility,
      imageUrl: resolvedImageUrl,
      isRenderCacheValid: Boolean(snapshotUrl),
      renderStateHash: nextRenderStateHash ?? undefined,
      status: 'CANDIDATE',
      parentId: effectiveParentId,
      annotationComments: sourceAnnotationVersion?.annotationComments
        ? sourceAnnotationVersion.annotationComments.map((comment) => ({ ...comment }))
        : [],
      lastAnnotationMaskUrl: undefined,
      annotationMask: null,
      annotationMaskIsDirty: false,
    };
    throwIfAborted(options.abortSignal);

    setWorkspaceStrict(capturedWorkspaceId, (workspace) => {
      let versions = pruneOldCandidates(workspace.versions || []);
      if (!workspace.validatedVersionId) {
        newVersion.status = 'VALIDATED';
        versions.push(newVersion);
        return {
          ...workspace,
          versions,
          validatedVersionId: newVersion.id,
          activeVersionId: newVersion.id,
        };
      }
      versions.push(newVersion);
      return { ...workspace, versions };
    });

    return newVersion;
  }, [kernel, renderAndUploadVersionSnapshot, computeVersionRenderStateHash]);

  const regenerateVersionVisuals = useCallback(async (
    workspaceId: string,
    versionId: string
  ): Promise<{ imageUrl: string }> => {
    if (!kernel.current) {
      throw new Error('[Versions] Kernel is not available to regenerate version visuals.');
    }

    const { getWorkspace, handleError } = kernel.current;
    const workspace = getWorkspace(workspaceId);

    if (!workspace) {
      const message = `[Versions] Workspace ${workspaceId} not found while refreshing visuals.`;
      console.error(message);
      throw new Error(message);
    }

    const targetVersion = workspace.versions?.find(v => v.id === versionId);
    if (!targetVersion) {
      const message = `[Versions] Version ${versionId} not found in workspace ${workspaceId}.`;
      console.error(message);
      throw new Error(message);
    }

    const previousCacheValid = Boolean(targetVersion.isRenderCacheValid);
    const expectedRenderStateHash = computeVersionRenderStateHash({
      workspaceId,
      layerVisibility: targetVersion.layerVisibility,
      rect: workspace.frameRect,
    });

    const setVersionCacheState = (isRenderCacheValid: boolean, imageUrl?: string) => {
      setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        versions: (currentWorkspace.versions || []).map((version) => {
          if (version.id !== versionId) return version;
          return {
            ...version,
            ...(imageUrl ? { imageUrl } : {}),
            isRenderCacheValid,
            ...(imageUrl ? { renderStateHash: expectedRenderStateHash ?? version.renderStateHash } : {}),
          };
        }),
      }));
    };

    setVersionCacheState(false);

    let newImageUrl: string | null = null;

    try {
      newImageUrl = await renderAndUploadVersionSnapshot({
        workspaceId,
        layerVisibility: targetVersion.layerVisibility,
        preferredAncestorVersionId: targetVersion.parentId ?? null,
        rect: workspace.frameRect,
        assetSource: 'version_view',
      });
    } catch (error) {
      console.error(`[Versions] Snapshot rendering failed for version ${versionId}.`, error);
      handleError?.('Failed to regenerate version preview.');
      setVersionCacheState(previousCacheValid);
      throw error instanceof Error ? error : new Error('Failed to regenerate version preview.');
    }

    if (!newImageUrl) {
      const message = `[Versions] Snapshot rendering returned null for version ${versionId}.`;
      console.error(message);
      handleError?.('Failed to regenerate version preview.');
      setVersionCacheState(previousCacheValid);
      throw new Error(message);
    }

    setVersionCacheState(true, newImageUrl);

    return { imageUrl: newImageUrl };
  }, [kernel, renderAndUploadVersionSnapshot, computeVersionRenderStateHash]);

  const getOrRenderVersionImage = useCallback(async (
    workspaceId: string,
    versionId: string,
    options?: { targetPixels?: number; assetSource?: CloudAssetSource; rect?: Rect }
  ): Promise<string | null> => {
    if (!kernel.current) return null;

    const { getWorkspace } = kernel.current;
    const workspace = getWorkspace(workspaceId);
    const version = workspace?.versions?.find(v => v.id === versionId);

    if (!workspace || !version) {
      console.error(`[Versions] Could not find workspace ${workspaceId} or version ${versionId} to render image.`);
      return null;
    }

    const imageUrl = await fetchVersionImageUrl(workspace, version, options);

    return imageUrl;
  }, [kernel, fetchVersionImageUrl]);

  const toggleVersionFavorite = useCallback((workspaceId: string, versionId: string) => {
    if (!kernel.current) return;
    const { getWorkspace, executeAction, logUserAction } = kernel.current;

    const workspace = getWorkspace(workspaceId);
    const version = workspace?.versions?.find(v => v.id === versionId);

    if (!workspace || !version) return;

    const newFavoriteStatus = !version.favorite;
    logUserAction('Version Favorite Toggled', { workspaceId, versionId, favorite: newFavoriteStatus });

    // Studio Cortex T3 (Phase 3): in the marketing view there is no promote/validate
    // action — favouriting IS the user's "keep this" pick. Persist it as the accept
    // signal so the Cortex reward can join it back to the loop's StudioEpisode.
    // Canvas keeps using promoteVersion; favourite there stays a light UI-only flag.
    if (kernel.current.getStudioViewMode() === 'marketing') {
      void markGenerationTaskAccepted(versionId, newFavoriteStatus);
      // The user reacted (favourited) — clear any pending proactive check-in.
    }

    const update = (isFav: boolean) => {
      setWorkspaceStrict(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        versions: (currentWorkspace.versions || []).map((candidateVersion) => {
          if (candidateVersion.id !== versionId) return candidateVersion;
          return { ...candidateVersion, favorite: isFav };
        }),
      }));
    };

    executeAction({
      description: `Toggle favorite for version ${version.name}`,
      redo: () => update(newFavoriteStatus),
      undo: () => update(!newFavoriteStatus)
    });
  }, [kernel]);

  return useMemo(() => ({
    ensureCurrentStateAsVersion,
    ensureWorkspaceVersionAfterCompositionCommit,
    ensureWritableAnnotationVersion,
    commitWorkspaceAnnotationMask,
    syncActiveWorkspaceVersionFromLayerVisibility,
    getActiveWorkspaceVersionId,
    saveWorkspaceVersionAnnotation,
    applyWorkspaceVersion,
    deleteWorkspaceVersion,
    deleteWorkspaceVersions,
    navigateWorkspaceVersion,
    promoteVersion,
    getVersionsForDisplay,
    rejectWorkspaceVersions,
    invalidateWorkspaceVersionCache,
    updateVersionCache,
    regenerateVersionVisuals,
    addCompletedGenerationAsVersion,
    getOrRenderVersionImage,
    toggleVersionFavorite
  }), [
    ensureCurrentStateAsVersion,
    ensureWorkspaceVersionAfterCompositionCommit,
    ensureWritableAnnotationVersion,
    commitWorkspaceAnnotationMask,
    syncActiveWorkspaceVersionFromLayerVisibility,
    getActiveWorkspaceVersionId,
    saveWorkspaceVersionAnnotation,
    applyWorkspaceVersion,
    deleteWorkspaceVersion,
    deleteWorkspaceVersions,
    navigateWorkspaceVersion,
    promoteVersion,
    getVersionsForDisplay,
    rejectWorkspaceVersions,
    invalidateWorkspaceVersionCache,
    updateVersionCache,
    regenerateVersionVisuals,
    addCompletedGenerationAsVersion,
    getOrRenderVersionImage,
    toggleVersionFavorite
  ]);
};

export default useVersions;
